import React, { useState, useEffect, useMemo, useCallback, useRef, useContext } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import {
  BarChart3, Users, Briefcase, DollarSign, PieChart as PieIcon,
  FileText, AlertTriangle, Clock, UserCheck, Pause, Building2,
  Receipt, TrendingUp, Target, Calendar, Download, Printer,
  ArrowUp, ArrowDown, Trophy, Menu as MenuIcon, X as XIcon, ArrowUpDown,
  Mail, Plus, Trash2, Send, Power, PowerOff, ShieldCheck, Search, Truck, MapPin, ShieldAlert, Tag,
  FolderKanban, Hammer, ChevronDown, ChevronRight, UserX, QrCode, AlarmClock, Hash, User,
} from 'lucide-react';
import { describeTzSpec } from '../utils/timezone';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import PayrollWorkbench from '../components/PayrollWorkbench';
import { useCompanyDefaults, readCompanyDefaults } from '../hooks/useCompanyDefaults';
import { loadPayrollView, savePayrollView, clearPayrollView } from '../utils/payrollView';
import PayPeriodConfigDialog from '../components/PayPeriodConfigDialog';
import DrillDrawer from '../components/reports/DrillDrawer';
import ChartSwitcher from '../components/reports/ChartSwitcher';
import { printBranded, downloadServerPdf } from '../components/reports/reportPrint';
import EmailPdfDialog from '../components/reports/EmailPdfDialog';
import SavedViewsMenu from '../components/reports/SavedViewsMenu';
import { ReportCtx } from '../components/reports/reportCtx';
import { Play, Loader2, MousePointerClick, FileDown } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

// Interactive reports (Sep 2026): drill-down clicks + branded print share this context.
// ReportCtx lives in components/reports/reportCtx.js (shared with PayrollWorkbench).
// Reports whose data does not depend on the selected date range.
const NO_RANGE_REPORTS = ['job-status-snapshot', 'overdue-jobs', 'unbilled-jobs', 'monthly-trend',
  'customer-job-history', 'paused-jobs', 'team-attendance-heatmap', 'worker-trend', 'tenure-turnover', 'stuck-shifts'];
// Module-level brand info so the legacy printReport() call sites become branded without prop threading.
const brandInfo = { company: null, user: null, period: '', chartEl: null };

// Which entity a table cell points at (only when the row carries the id).
function drillTargetFor(row, colKey) {
  const k = colKey;
  const wid = row.worker_id || row.user_id;
  if (wid && (/^(worker_name|user_name|worker|employee)$/.test(k) || (k === 'name' && !row.job_number && !row.project_number))) return { type: 'worker', id: wid, label: row[k] };
  const jid = row.job_id || (row.job_number && row.id);
  if (jid && /^(title|job_title|job_number|job|job_name)$/.test(k)) return { type: 'job', id: jid, label: row[k] };
  if (row.customer_id && /^(customer_name|customer)$/.test(k)) return { type: 'customer', id: row.customer_id, label: row[k] };
  const pid = row.project_id || (row.project_number && row.id);
  if (pid && /^(project_name|project_number|project|name)$/.test(k)) return { type: 'project', id: pid, label: row[k] };
  if (k === 'group') {
    if (row.project_id) return { type: 'project', id: row.project_id, label: row[k] };
    if (row.job_id) return { type: 'job', id: row.job_id, label: row[k] };
  }
  return null;
}

// Job Margins drill-down dialog (Jun 2026)
const MarginDrillDialog = ({ drill, loading, onClose, money, marginColor, dd }) => (
  <Dialog open={!!drill} onOpenChange={(o) => { if (!o) onClose(); }}>
    <DialogContent className="max-w-2xl" data-testid="margin-drill-dialog">
      <DialogHeader>
        <DialogTitle>{drill?.row?.job_number} — {drill?.row?.title || 'Job'}</DialogTitle>
        <DialogDescription>{drill?.row?.customer_name || 'No customer'} · what makes up this job's cost</DialogDescription>
      </DialogHeader>
      {loading && <p className="text-sm py-6 text-center" style={{ color: '#A0AEC0' }}>Loading breakdown…</p>}
      {dd?.error && <p className="text-sm py-4 text-center" style={{ color: '#EF4444' }}>{dd.error}</p>}
      {dd && !dd.error && (
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          <div className="flex flex-wrap gap-3" data-testid="drill-summary">
            {[
              ['Revenue', money(dd.summary.revenue), '#1E88E5'],
              ['Labor', money(dd.summary.labor_cost), '#FFA000'],
              ['Materials', money(dd.summary.material_cost), '#FFA000'],
              ['Margin', money(dd.summary.margin), marginColor(dd.summary.margin)],
            ].map(([l, v, c]) => (
              <div key={l} className="px-3 py-1.5 rounded-lg" style={{ background: '#1A2433', border: '1px solid #2D3748' }}>
                <p className="text-[10px] uppercase tracking-wider" style={{ color: '#64748B' }}>{l}</p>
                <p className="text-sm font-bold" style={{ color: c }}>{v}</p>
              </div>
            ))}
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold mb-1.5" style={{ color: '#64748B' }}>Labor entries ({dd.labor.length})</p>
            {dd.labor_note && <p className="text-xs mb-2 p-2 rounded" style={{ color: '#FFA000', background: 'rgba(255,160,0,0.08)' }} data-testid="drill-labor-note">{dd.labor_note}</p>}
            {dd.labor.length > 0 ? (
              <table className="w-full text-xs" data-testid="drill-labor-table">
                <thead><tr style={{ color: '#64748B' }} className="text-left">
                  <th className="py-1 pr-2">Date</th><th className="py-1 pr-2">Worker</th>
                  <th className="py-1 pr-2 text-right">Hours</th><th className="py-1 pr-2 text-right">Rate</th>
                  <th className="py-1 text-right">Cost</th>
                </tr></thead>
                <tbody>
                  {dd.labor.map((l, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #2D3748', color: '#E2E8F0' }}>
                      <td className="py-1 pr-2">{l.date}</td>
                      <td className="py-1 pr-2">{l.worker}{l.open ? <span style={{ color: '#FFA000' }}> (open)</span> : ''}</td>
                      <td className="py-1 pr-2 text-right">{l.hours}</td>
                      <td className="py-1 pr-2 text-right">${l.rate.toFixed(2)}</td>
                      <td className="py-1 text-right">{money(l.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (!dd.labor_note && <p className="text-xs" style={{ color: '#64748B' }}>No labor logged on this job.</p>)}
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold mb-1.5" style={{ color: '#64748B' }}>Materials ({dd.materials.length})</p>
            {dd.materials.length > 0 ? (
              <table className="w-full text-xs" data-testid="drill-materials-table">
                <thead><tr style={{ color: '#64748B' }} className="text-left">
                  <th className="py-1 pr-2">Item</th><th className="py-1 pr-2 text-right">Qty</th>
                  <th className="py-1 pr-2 text-right">Unit cost</th><th className="py-1 pr-2 text-right">Cost total</th>
                  <th className="py-1 text-right">Billed</th>
                </tr></thead>
                <tbody>
                  {dd.materials.map((m, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #2D3748', color: '#E2E8F0' }}>
                      <td className="py-1 pr-2">{m.description}</td>
                      <td className="py-1 pr-2 text-right">{m.quantity}</td>
                      <td className="py-1 pr-2 text-right">{money(m.cost)}</td>
                      <td className="py-1 pr-2 text-right">{money(m.cost_total)}</td>
                      <td className="py-1 text-right">{money(m.billed_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="text-xs" style={{ color: '#64748B' }}>No materials on this job.</p>}
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const CATS = [
  {
    label: 'Staff & Attendance',
    items: [
      { key: 'attendance', title: 'Attendance Report', icon: UserCheck },
      { key: 'attendance-individual', title: 'Individual Attendance', icon: User },
      { key: 'no-shows', title: 'No-Shows & Absences', icon: UserX },
      { key: 'worker-performance', title: 'Worker Performance', icon: Target },
      { key: 'staff-hours-summary', title: 'Staff Hours Summary', icon: Clock },
      { key: 'top-performers', title: 'Top Performers', icon: Trophy },
    ],
  },
  {
    label: 'Jobs & Operations',
    items: [
      { key: 'completed-jobs', title: 'Completed Jobs', icon: Briefcase },
      { key: 'job-status-snapshot', title: 'Job Status Snapshot', icon: PieIcon },
      { key: 'overdue-jobs', title: 'Overdue Jobs', icon: AlertTriangle },
      { key: 'paused-jobs', title: 'Paused Jobs', icon: Pause },
      { key: 'customer-job-history', title: 'Customer Job History', icon: Building2 },
    ],
  },
  {
    label: 'Financial & Payroll',
    items: [
      { key: 'payroll', title: 'Payroll Report', icon: DollarSign },
      { key: 'hours-review', title: 'Hours Review (Regular / OT / Call-outs)', icon: Clock },
      { key: 'billable-hours', title: 'Billable Hours', icon: PieIcon },
      { key: 'service-rate', title: 'Service Rate Calculator', icon: DollarSign },
      { key: 'job-margins', title: 'Job Margins', icon: TrendingUp },
      { key: 'billing', title: 'Billing Report', icon: Receipt },
      { key: 'invoiced-jobs', title: 'Invoiced Jobs', icon: Receipt },
      { key: 'invoice-register', title: 'Invoice Register', icon: Hash },
      { key: 'revenue-by-customer', title: 'Revenue by Customer', icon: TrendingUp },
      { key: 'label-summary', title: 'Hours by Label', icon: Tag },
      { key: 'hours-by-project', title: 'Hours by Project', icon: FolderKanban },
      { key: 'hours-by-location', title: 'Hours by Location', icon: MapPin },
      { key: 'hours-by-job', title: 'Hours by Job', icon: Hammer },
      { key: 'unbilled-jobs', title: 'Unbilled Jobs', icon: FileText },
    ],
  },
  {
    label: 'KPI Summary',
    items: [
      { key: 'company-kpi', title: 'Company KPI Summary', icon: BarChart3 },
      { key: 'team-productivity', title: 'Team Productivity', icon: Users },
      { key: 'monthly-trend', title: 'Monthly Trend', icon: Calendar },
    ],
  },
  {
    label: 'HR & Executive',
    items: [
      { key: 'team-attendance-heatmap', title: 'Team Attendance Heatmap', icon: Calendar },
      { key: 'worker-variance', title: 'Worker Variance Report', icon: Target },
      { key: 'team-scorecard', title: 'Team-vs-Team Scorecard', icon: BarChart3 },
      { key: 'worker-trend', title: 'Worker Historical Trend', icon: TrendingUp },
      { key: 'cost-per-job-by-team', title: 'Cost per Job by Team', icon: DollarSign },
      { key: 'tenure-turnover', title: 'Tenure & Turnover', icon: Users },
    ],
  },
  {
    label: 'Safety & Compliance',
    items: [
      { key: 'safety-attestations', title: 'Safety Attestations', icon: ShieldCheck },
      { key: 'travel-time', title: 'Travel vs On-site Time', icon: Truck },
      { key: 'unverified-clockins', title: 'Unverified Clock-Ins', icon: ShieldAlert },
      { key: 'qr-scan-activity', title: 'QR Scan Activity', icon: QrCode },
      { key: 'stuck-shifts', title: 'Stuck Shifts', icon: AlarmClock },
    ],
  },
];

const CHART_COLORS = ['#1E88E5', '#00C896', '#FFA000', '#EF4444', '#A78BFA', '#64B5F6', '#10B981'];

// Build date range from preset
function rangePreset(preset) {
  const now = new Date();
  const ymd = (d) => d.toISOString().slice(0, 10);
  if (preset === 'this_week') {
    const day = now.getUTCDay() || 7;
    const start = new Date(now); start.setUTCDate(now.getUTCDate() - (day - 1));
    return { start: ymd(start), end: ymd(now) };
  }
  if (preset === 'this_month') {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    return { start: ymd(start), end: ymd(now) };
  }
  if (preset === 'last_month') {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
    return { start: ymd(start), end: ymd(end) };
  }
  if (preset === 'last_3_months') {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 3, 1));
    return { start: ymd(start), end: ymd(now) };
  }
  return null;
}

// CSV export helper
function downloadCSV(filename, rows, columns, extraMeta = [], reportTitle = null) {
  if (!rows || rows.length === 0) return;
  const header = columns.map((c) => c.label).join(',');
  const esc = (v) => {
    if (v == null) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const cell = (c, r) => {
    if (typeof c.value === 'function') return c.value(r);
    if (typeof c.render === 'function') {
      const out = c.render(r);
      if (typeof out === 'string' || typeof out === 'number') return out;
    }
    return r[c.key];
  };
  const body = rows.map((r) => columns.map((c) => esc(cell(c, r))).join(',')).join('\n');
  const meta = [
    `Report,${esc(`${brandInfo.company?.company_name || brandInfo.company?.name || ''} — ${reportTitle || filename.replace(/\.csv$/, '').replace(/_/g, ' ')}`)}`,
    `Period,${esc(brandInfo.period || 'All time')}`,
    `Generated,${esc(`${new Date().toLocaleString()} by ${brandInfo.user?.name || ''}`)}`,
    ...extraMeta.map(([k, v]) => `${esc(k)},${esc(v)}`),
    '',
  ].join('\n');
  const csv = `${meta}\n${header}\n${body}`;
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// Print current report — branded (logo, colours, period, footer) → browser print / Save as PDF.
function printReport(containerRef, title, summary = []) {
  printBranded({ containerRef, chartEl: brandInfo.chartEl, title, period: brandInfo.period, company: brandInfo.company, user: brandInfo.user, summary });
}

import StaffReports from './StaffReports';
import ServiceRatePane from '../components/reports/ServiceRatePane';
import HoursReviewPane from '../components/reports/HoursReviewPane';

export default function ReportsEnhanced() {
  const { token, isAdmin, isSupervisor, loading, user } = useAuth();
  const { defaults: companyDefaults, loaded: defaultsLoaded } = useCompanyDefaults(token);
  const [active, setActive] = useState(() => {
    const want = readCompanyDefaults().reports_default_report;
    return CATS.some((c) => c.items.some((it) => it.key === want)) ? want : 'company-kpi';
  });
  const [preset, setPreset] = useState('this_month');
  const [rng, setRng] = useState(() => rangePreset('this_month'));
  const [customOpen, setCustomOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);
  const [payInfo, setPayInfo] = useState(null);
  const [payCfgOpen, setPayCfgOpen] = useState(false);
  // Interactive reports (Sep 2026): explicit Run + status line, drill-down drawer, custom dates as a draft.
  const [draft, setDraft] = useState(null);
  const [runId, setRunId] = useState(0);
  const [status, setStatus] = useState({ loading: false, at: null, rows: null });
  const [drill, setDrill] = useState(null);
  const [drillFilters, setDrillFilters] = useState({});
  const { company } = useAuth();
  useEffect(() => { brandInfo.company = company; brandInfo.user = user; }, [company, user]);
  useEffect(() => { brandInfo.period = rng ? `${rng.start} → ${rng.end}` : 'All time'; }, [rng]);
  const onDrill = useCallback((t) => setDrill(t), []);
  const onStatus = useCallback((st) => setStatus((prev) => ({ ...prev, ...st })), []);
  const drillNavigate = (reportKey, filters = {}) => { setDrill(null); setDrillFilters(filters); setActive(reportKey); };
  const ctxValue = useMemo(() => ({ onDrill, company, user, period: rng ? `${rng.start} → ${rng.end}` : '', token }), [onDrill, company, user, rng, token]);

  const canAccess = isAdmin || isSupervisor;

  // Dashboard deep-links: /reports?preset=today lands on today's attendance.
  const location = useLocation();
  const urlPresetRef = useRef(false);
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const rep = p.get('report');
    if (rep && CATS.some((c) => c.items.some((it) => it.key === rep))) {
      urlPresetRef.current = true;
      setActive(rep);
    }
    // Manage Users → "Attendance" button: /reports?report=attendance-individual&user_id=…&name=…
    const uid = p.get('user_id');
    if (uid) {
      urlPresetRef.current = true;
      setActive('attendance-individual');
      setDrillFilters({ worker: uid, _from: p.get('name') ? `Manage Users · ${p.get('name')}` : 'Manage Users', _t: Date.now() });
      const rp = p.get('range');
      const r = rp ? rangePreset(rp) : null;
      if (r) { setPreset(rp); setRng(r); }
    }
    if (p.get('preset') === 'today') {
      const t = new Date().toISOString().slice(0, 10);
      urlPresetRef.current = true;
      setPreset('custom');
      setRng({ start: t, end: t });
      setActive('attendance');
    }
  }, []);

  // Pay-period presets (Jun 2026): fetch the company cutoff cycle once.
  useEffect(() => {
    if (!token || loading || !canAccess) return undefined;
    let alive = true;
    axios
      .get(`${API}/company/pay-period`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (alive) setPayInfo(data); })
      .catch(() => { if (alive) setPayInfo({ configured: false }); });
    return () => { alive = false; };
  }, [token, loading, canAccess]);

  // Apply the company's default date range once pay-period info + defaults are
  // known (Settings → Company Defaults → Reports). Pay-period ranges fall back
  // to "this month" when no cycle is configured.
  const rangeAppliedRef = useRef(false);
  useEffect(() => {
    if (!payInfo || !defaultsLoaded || rangeAppliedRef.current || urlPresetRef.current || payrollRangeRestoredRef.current) return;
    rangeAppliedRef.current = true;
    const want = companyDefaults.reports_default_range || 'this_pay_period';
    if (want === 'this_pay_period' || want === 'last_pay_period') {
      if (!payInfo.configured) return;
      const per = want === 'this_pay_period' ? payInfo.current : payInfo.previous;
      if (per?.start) { setPreset(want); setRng({ start: per.start, end: per.end }); }
      return;
    }
    const r = rangePreset(want);
    if (r) { setPreset(want); setRng(r); }
  }, [payInfo, defaultsLoaded, companyDefaults.reports_default_range]);

  // Company default landing report (only before the manager picks one).
  const landingAppliedRef = useRef(false);
  useEffect(() => {
    if (!defaultsLoaded || landingAppliedRef.current || urlPresetRef.current) return;
    landingAppliedRef.current = true;
    const want = companyDefaults.reports_default_report;
    if (want && CATS.some((c) => c.items.some((it) => it.key === want))) setActive(want);
  }, [defaultsLoaded, companyDefaults.reports_default_report]);

  // Remember Payroll place (Sep 2026): restore the saved date range when the
  // Payroll Report opens, keep saving it while it is active.
  const rememberPayroll = !!companyDefaults.payroll_remember_view;
  const payrollRestoredRef = useRef(false);
  const payrollRangeRestoredRef = useRef(false);
  useEffect(() => {
    if (active !== 'payroll' || !rememberPayroll || payrollRestoredRef.current) return;
    payrollRestoredRef.current = true;
    const saved = loadPayrollView(user?.id);
    if (saved.preset && saved.rng?.start && saved.rng?.end) { payrollRangeRestoredRef.current = true; setPreset(saved.preset); setRng(saved.rng); }
  }, [active, rememberPayroll, user?.id]);
  useEffect(() => {
    if (active === 'payroll' && rememberPayroll && payrollRestoredRef.current && rng?.start) savePayrollView(user?.id, { preset, rng });
  }, [active, rememberPayroll, preset, rng, user?.id]);

  // NOTE: all hooks must run before any conditional return — putting this
  // useMemo below the loading/canAccess returns crashed with React #310
  // ("rendered more hooks") on every hard refresh of /reports (Jun 2026).
  const activeMeta = useMemo(() => {
    for (const c of CATS) for (const it of c.items) if (it.key === active) return it;
    return null;
  }, [active]);

  if (loading) {
    return <div className="p-8 text-center" style={{ color: '#A0AEC0' }}>Loading…</div>;
  }
  // Staff see their personal self-service reports at the same /reports URL
  if (!canAccess) {
    return <StaffReports />;
  }

  const setPresetAndRange = (p) => {
    if (p === 'this_pay_period' || p === 'last_pay_period') {
      if (!payInfo?.configured) {
        if (isAdmin) setPayCfgOpen(true);
        else window.alert('No pay period cycle is configured yet — ask an admin to set it via the "Pay Period" button on the Reports page.');
        return;
      }
      setPreset(p);
      setCustomOpen(false);
      const per = p === 'this_pay_period' ? payInfo.current : payInfo.previous;
      setRng({ start: per.start, end: per.end });
      return;
    }
    setPreset(p);
    if (p === 'custom') { setDraft(rng || rangePreset('this_month')); setCustomOpen(true); return; }
    const r = rangePreset(p);
    if (r) setRng(r);
    setCustomOpen(false);
  };
  const runReport = () => {
    if (customOpen && draft?.start && draft?.end) {
      if (draft.start > draft.end) { window.alert('Start date must be on or before the end date.'); return; }
      setRng({ start: draft.start, end: draft.end });
    }
    setRunId((n) => n + 1);
  };
  const draftDirty = customOpen && draft && (draft.start !== rng?.start || draft.end !== rng?.end);
  const rangeIgnored = NO_RANGE_REPORTS.includes(active);
  // Shared saved views (Sep 2026): report + period + chart type + drill filters.
  const applyView = (v) => {
    setDrill(null);
    setDrillFilters({ ...(v.filters || {}), chart_view: v.chart_view || 'table', _t: Date.now() });
    if (v.preset === 'custom' && v.start_date && v.end_date) { setPreset('custom'); setDraft({ start: v.start_date, end: v.end_date }); setCustomOpen(true); setRng({ start: v.start_date, end: v.end_date }); }
    else if (v.preset && rangePreset(v.preset)) { setPreset(v.preset); setCustomOpen(false); setRng(rangePreset(v.preset)); }
    setActive(v.report_key);
  };
  const currentView = {
    suggestedName: `${activeMeta?.title || active} — ${preset === 'custom' && rng ? `${rng.start} → ${rng.end}` : preset.replace(/_/g, ' ')}`,
    payload: { report_key: active, preset, start_date: preset === 'custom' ? rng?.start : null, end_date: preset === 'custom' ? rng?.end : null,
      chart_view: status.chart_view || 'table', filters: status.filters || {} },
  };

  return (
    <ReportCtx.Provider value={ctxValue}>
    <div className="min-h-full grid grid-cols-1 md:grid-cols-[260px_1fr] gap-5 p-4 md:p-6" data-testid="reports-page">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:block md:sticky md:top-4 self-start rounded-xl p-2"
             style={{ background: '#0F1923', border: '1px solid #2D3748' }}
             data-testid="reports-sidebar">
        <ReportsSidebar active={active} onSelect={(k) => setActive(k)} />
      </aside>

      {/* Mobile nav */}
      <div className="md:hidden flex items-center justify-between">
        <h1 className="text-white font-semibold">Reports</h1>
        <button onClick={() => setMobileNav(true)} className="p-2 rounded-lg bg-white/5"
                data-testid="reports-mobile-nav-open" aria-label="Open reports menu">
          <MenuIcon className="w-5 h-5 text-white" />
        </button>
      </div>
      {mobileNav && (
        <div className="fixed inset-0 z-[100] md:hidden" onClick={() => setMobileNav(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="absolute left-0 top-0 bottom-0 w-80 bg-[#0F1923] border-r border-[#2D3748] p-3 overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3 px-2">
              <h2 className="text-white font-semibold">Reports</h2>
              <button onClick={() => setMobileNav(false)} className="text-slate-400"><XIcon className="w-5 h-5" /></button>
            </div>
            <ReportsSidebar active={active} onSelect={(k) => { setActive(k); setMobileNav(false); }} />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="min-w-0 space-y-4">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl"
             style={{ background: '#0F1923', border: '1px solid #2D3748' }}
             data-testid="reports-toolbar">
          <div>
            <h2 className="text-lg font-semibold text-white">{activeMeta?.title || 'Report'}</h2>
            <p className="text-xs flex flex-wrap items-center gap-x-2" style={{ color: '#64748B' }} data-testid="report-status-line">
              <span>{rangeIgnored ? 'Not date-filtered' : (rng ? `${rng.start} → ${rng.end}` : 'All time')}</span>
              {status.loading ? (
                <span className="inline-flex items-center gap-1" style={{ color: '#1E88E5' }} data-testid="report-status-loading"><Loader2 className="w-3 h-3 animate-spin" /> Applying…</span>
              ) : status.at ? (
                <span data-testid="report-status-updated">· Updated {new Date(status.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}{status.rows != null ? ` · ${status.rows} row${status.rows === 1 ? '' : 's'}` : ''}</span>
              ) : null}
              {active === 'attendance' ? (
                <span className="inline-flex items-center gap-1" title="Click a worker's name for their individual attendance report"><MousePointerClick className="w-3 h-3" /> click a name for their individual report</span>
              ) : active !== 'attendance-individual' && (
                <span className="inline-flex items-center gap-1" title="Worker, job, customer and project names are clickable"><MousePointerClick className="w-3 h-3" /> click a name to drill in</span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {['this_pay_period', 'last_pay_period', 'this_week', 'this_month', 'last_month', 'last_3_months', 'custom'].map((p) => (
              <button
                key={p}
                onClick={() => setPresetAndRange(p)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{
                  background: preset === p ? '#1E88E5' : 'transparent',
                  color: preset === p ? '#fff' : '#A0AEC0',
                  border: `1px solid ${preset === p ? '#1E88E5' : '#2D3748'}`,
                }}
                data-testid={`preset-${p}`}
              >
                {p === 'this_pay_period' ? 'This Pay Period' : p === 'last_pay_period' ? 'Last Pay Period' :
                 p === 'this_week' ? 'This Week' : p === 'this_month' ? 'This Month' :
                 p === 'last_month' ? 'Last Month' : p === 'last_3_months' ? 'Last 3 Months' : 'Custom'}
              </button>
            ))}
            <button
              onClick={runReport}
              disabled={status.loading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-60"
              style={{ background: draftDirty ? '#00C896' : '#1A2433', color: draftDirty ? '#0B1321' : '#fff', border: `1px solid ${draftDirty ? '#00C896' : '#2D3748'}` }}
              data-testid="run-report-btn"
              title={draftDirty ? 'Apply the custom dates' : 'Re-run this report'}
            >
              {status.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} {draftDirty ? 'Apply dates' : 'Run report'}
            </button>
            {isAdmin && (
              <button
                onClick={() => setPayCfgOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{ background: '#1A2433', color: '#CBD5E1', border: '1px solid #2D3748' }}
                data-testid="pay-period-config-btn"
              >
                <Calendar className="w-3.5 h-3.5" /> Pay Period
              </button>
            )}
            <SavedViewsMenu token={token} current={currentView} onApply={applyView} isAdmin={isAdmin} userId={user?.id} />
            {isAdmin && (
              <button
                onClick={() => setManagerOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{ background: '#1A2433', color: '#CBD5E1', border: '1px solid #2D3748' }}
                data-testid="manage-schedules-btn"
              >
                <Mail className="w-3.5 h-3.5" /> Manage Schedules
              </button>
            )}
          </div>
        </div>

        {customOpen && (
          <div className="p-3 rounded-xl flex flex-wrap items-center gap-3" style={{ background: '#0F1923', border: '1px solid #2D3748' }} data-testid="custom-range-bar">
            <label className="text-xs text-slate-400">Start
              <input type="date" value={draft?.start || ''} onChange={(e) => setDraft((r) => ({ ...(r || {}), start: e.target.value }))}
                className="ml-2 px-2 py-1 rounded bg-[#1A2433] border border-[#2D3748] text-white text-xs" data-testid="custom-start" />
            </label>
            <label className="text-xs text-slate-400">End
              <input type="date" value={draft?.end || ''} onChange={(e) => setDraft((r) => ({ ...(r || {}), end: e.target.value }))}
                className="ml-2 px-2 py-1 rounded bg-[#1A2433] border border-[#2D3748] text-white text-xs" data-testid="custom-end" />
            </label>
            <button onClick={runReport} disabled={!draft?.start || !draft?.end || status.loading}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50" style={{ background: '#00C896', color: '#0B1321' }} data-testid="apply-custom-range-btn">
              Apply dates
            </button>
            {draftDirty && <span className="text-[11px]" style={{ color: '#FFA000' }} data-testid="custom-range-pending">Dates changed — click Apply to refresh the report</span>}
          </div>
        )}
        {rangeIgnored && (
          <p className="text-[11px] px-3" style={{ color: '#64748B' }} data-testid="range-ignored-note">This report shows current data and is not filtered by the selected period.</p>
        )}
        {drillFilters._from && (
          <div className="px-3 flex items-center gap-2 text-[11px]" data-testid="filtered-from-chip">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ background: 'rgba(30,136,229,0.15)', color: '#93C5FD', border: '1px solid rgba(30,136,229,0.4)' }}>
              Filtered from: {drillFilters._from}
              <button type="button" onClick={() => setDrillFilters({ _clear: true, _t: Date.now() })} className="ml-1 hover:text-white" title="Clear inherited filter" data-testid="filtered-from-clear">×</button>
            </span>
          </div>
        )}

        <ReportPane
          reportKey={active}
          token={token}
          startDate={rng?.start}
          endDate={rng?.end}
          title={activeMeta?.title || 'Report'}
          runId={runId}
          onStatus={onStatus}
          presetFilters={drillFilters}
        />
      </div>

      {drill && <DrillDrawer target={drill} range={rng} token={token} onClose={() => setDrill(null)} onNavigate={drillNavigate} />}
      {managerOpen && <SchedulesManager token={token} onClose={() => setManagerOpen(false)} />}

      <PayPeriodConfigDialog
        open={payCfgOpen}
        onClose={() => setPayCfgOpen(false)}
        token={token}
        initial={payInfo?.config}
        onSaved={(data) => {
          setPayInfo(data);
          setPreset('this_pay_period');
          setCustomOpen(false);
          setRng({ start: data.current.start, end: data.current.end });
        }}
      />
    </div>
    </ReportCtx.Provider>
  );
}

/* ---------- Sidebar ---------- */
function ReportsSidebar({ active, onSelect }) {
  return (
    <div className="space-y-3">
      {CATS.map((cat) => (
        <div key={cat.label}>
          <div className="px-2 pt-1 pb-1 text-[10px] uppercase tracking-wider" style={{ color: '#64748B' }}>
            {cat.label}
          </div>
          <ul>
            {cat.items.map((it) => {
              const Icon = it.icon;
              const isOn = active === it.key;
              return (
                <li key={it.key}>
                  <button
                    onClick={() => onSelect(it.key)}
                    className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-xs text-left"
                    style={{
                      background: isOn ? 'rgba(30,136,229,0.15)' : 'transparent',
                      color: isOn ? '#1E88E5' : '#CBD5E1',
                    }}
                    data-testid={`report-nav-${it.key}`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" style={{ color: isOn ? '#1E88E5' : '#64748B' }} />
                    <span className="flex-1 truncate">{it.title}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

/* ---------- Shell wrapping each report with CSV + PDF + Schedule ---------- */
function ReportShell({ title, onExportCSV, onPrint, onPdf, onEmail, onSchedule, children, dataTestId }) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const runPdf = async () => {
    if (!onPdf || pdfBusy) return;
    setPdfBusy(true);
    try { await onPdf(); } catch (e) { window.alert(e?.response?.data?.detail || 'PDF export failed'); } finally { setPdfBusy(false); }
  };
  return (
    <div className="rounded-xl overflow-hidden" style={{ background: '#0F1923', border: '1px solid #2D3748' }}
         data-testid={dataTestId}>
      <div className="flex items-center justify-between p-3 border-b border-[#2D3748]">
        <div className="text-sm font-medium text-white">{title}</div>
        <div className="flex items-center gap-2">
          {onSchedule && (
            <button onClick={onSchedule}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                    style={{ background: '#1E88E5', color: 'white' }}
                    data-testid="schedule-report-btn">
              <Mail className="w-3.5 h-3.5" /> Schedule Email
            </button>
          )}
          <button onClick={onExportCSV}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                  style={{ background: '#00C896', color: '#0B1321' }}
                  data-testid="export-csv-btn">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          {onEmail && (
            <button onClick={onEmail}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                    style={{ background: '#1A2433', color: 'white', border: '1px solid #2D3748' }}
                    data-testid="send-pdf-btn" title="Email the branded PDF to staff or any address">
              <Send className="w-3.5 h-3.5" /> Send PDF
            </button>
          )}
          {onPdf && (
            <button onClick={runPdf} disabled={pdfBusy}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-60"
                    style={{ background: '#1A2433', color: 'white', border: '1px solid #2D3748' }}
                    data-testid="export-pdf-btn" title="Branded PDF generated on the server">
              {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} Export PDF
            </button>
          )}
          <button onClick={onPrint}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                  style={{ background: '#2D3748', color: 'white' }}
                  data-testid="print-pdf-btn" title="Branded print — choose 'Save as PDF' in the print dialog">
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
        </div>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function EmptyState({ msg = 'No data found for this period. Try adjusting the date range.' }) {
  return (
    <div className="py-12 text-center" data-testid="empty-state">
      <p style={{ color: '#64748B' }}>{msg}</p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-2 animate-pulse" data-testid="skeleton">
      {[...Array(6)].map((_, i) => (
        <div key={`skeleton-${i}`} className="h-8 rounded" style={{ background: '#1A2433' }} />
      ))}
    </div>
  );
}

/* ---------- Sortable table ---------- */
function DataTable({ rows, columns, className }) {
  const { onDrill } = useContext(ReportCtx);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const out = [...rows];
    out.sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return out;
  }, [rows, sortKey, sortDir]);

  const handleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('asc'); }
  };

  return (
    <div className={`overflow-x-auto ${className || ''}`}>
      <table className="w-full text-sm">
        <thead>
          <tr style={{ borderBottom: '1px solid #2D3748' }}>
            {columns.map((c) => (
              <th key={c.key} className="text-left px-3 py-2 font-medium text-xs uppercase tracking-wider"
                  style={{ color: '#64748B', whiteSpace: 'nowrap' }}>
                <button onClick={() => handleSort(c.key)} className="flex items-center gap-1 hover:text-white">
                  {c.label}
                  <ArrowUpDown className="w-3 h-3 opacity-60" />
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={`${r.id || r.worker_id || 'row'}-${i}`} style={{ borderBottom: '1px solid #1A2433', ...r._rowStyle }}>
              {columns.map((c) => {
                const target = onDrill && !c.noDrill ? drillTargetFor(r, c.key) : null;
                const content = c.render ? c.render(r) : (r[c.key] ?? '-');
                return (
                  <td key={c.key} className="px-3 py-2 text-white"
                      style={{ whiteSpace: 'nowrap', ...(c.cellStyle?.(r) || {}) }}>
                    {target ? (
                      <button type="button" onClick={() => onDrill(target)}
                              className="text-left text-sky-300 hover:text-sky-200 hover:underline underline-offset-2"
                              data-testid={`drill-${target.type}-${target.id}`} title={`Open ${target.type} details`}>
                        {content}
                      </button>
                    ) : content}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Formatters ---------- */
const parseDateOnly = (v) => {
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) { const [y, m, d] = v.split('-').map(Number); return new Date(y, m - 1, d); }
  return new Date(v);
};
const fmtDate = (v) => v ? parseDateOnly(v).toLocaleDateString() : '-';
const fmtDT = (v) => v ? new Date(v).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '-';
const fmtMoney = (v) => v == null ? '-' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtHours = (v) => v == null ? '-' : `${Number(v).toFixed(2)} h`;

/* ---------- Hours by Project / Hours by Job (payroll vs job-session comparison) ---------- */
const HC_PIE_COLORS = ['#1E88E5', '#00C896', '#FFA000', '#A78BFA', '#EF4444', '#2DD4BF', '#F472B6', '#94A3B8'];

function HoursComparisonPane({ reportKey, title, data, startDate, endDate, printRef, scheduleOpen, setScheduleOpen, token,
  customers = [], customerId, setCustomerId, varThreshold, setVarThreshold }) {
  const { onDrill: hcDrill } = useContext(ReportCtx);
  const [expanded, setExpanded] = useState(() => new Set());
  const [chartType, setChartType] = useState('bar');
  const [onlyAlerts, setOnlyAlerts] = useState(false);
  const isProject = reportKey === 'hours-by-project';
  const allRows = data?.rows || [];
  const rows = onlyAlerts ? allRows.filter((r) => r.variance_alert) : allRows;
  const totals = data?.totals || {};

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const groupLabel = (r) => isProject
    ? (r.project_number ? `${r.project_number} — ${r.name}` : r.name)
    : (r.job_number ? `${r.job_number} — ${r.title}` : r.title);

  const varianceColor = (v) => Math.abs(v || 0) < 0.05 ? '#00C896' : Math.abs(v || 0) <= 1 ? '#FFA000' : '#EF4444';

  const summaryCols = [
    ...(isProject
      ? [{ key: 'project_number', label: 'Project #' }, { key: 'name', label: 'Project' }]
      : [{ key: 'job_number', label: 'Job #' }, { key: 'title', label: 'Job' }]),
    { key: 'customer_name', label: 'Customer' },
    ...(isProject ? [{ key: 'jobs_count', label: 'Jobs' }] : []),
    { key: 'worker_count', label: 'Workers' },
    { key: 'payroll_hours', label: 'Payroll Hours', render: (r) => fmtHours(r.payroll_hours) },
    { key: 'job_hours', label: 'Job Hours', render: (r) => fmtHours(r.job_hours) },
    { key: 'variance', label: 'Variance', render: (r) => (
      <span className="inline-flex items-center gap-1">
        {r.variance_alert && <AlertTriangle className="w-3.5 h-3.5" style={{ color: '#EF4444' }} data-testid={`hc-alert-flag-${r.id}`} />}
        {`${r.variance > 0 ? '+' : ''}${(r.variance ?? 0).toFixed(2)} h`}
      </span>
    ) },
    { key: 'labor_cost', label: 'Est. Labor Cost', render: (r) => r.labor_cost != null ? fmtMoney(r.labor_cost) : '' },
  ];

  const buildDetailedRows = () => rows.flatMap((r) => (r.workers || []).map((w) => ({
    group: groupLabel(r),
    customer_name: r.customer_name || '',
    worker_name: w.worker_name,
    payroll_hours: w.payroll_hours,
    job_hours: w.job_hours,
    variance: w.variance,
    hourly_rate: w.hourly_rate,
    labor_cost: w.labor_cost,
  })));
  const detailedCols = [
    { key: 'group', label: isProject ? 'Project' : 'Job' },
    { key: 'customer_name', label: 'Customer' },
    { key: 'worker_name', label: 'Worker' },
    { key: 'payroll_hours', label: 'Payroll Hours' },
    { key: 'job_hours', label: 'Job Hours' },
    { key: 'variance', label: 'Variance (h)' },
    { key: 'hourly_rate', label: 'Hourly Rate', render: (r) => r.hourly_rate != null ? r.hourly_rate.toFixed(2) : '' },
    { key: 'labor_cost', label: 'Labor Cost ($)', render: (r) => r.labor_cost != null ? r.labor_cost.toFixed(2) : '' },
  ];

  const chartData = rows.slice(0, 10).map((r) => ({
    name: isProject ? (r.project_number || r.name) : (r.job_number || r.title),
    'Payroll Hours': r.payroll_hours,
    'Job Hours': r.job_hours,
  }));
  const pieData = (() => {
    const anyPayroll = rows.some((r) => r.payroll_hours > 0);
    const val = (r) => anyPayroll ? r.payroll_hours : r.job_hours;
    const top = rows.slice(0, 8).map((r) => ({ name: isProject ? (r.project_number || r.name) : (r.job_number || r.title), value: val(r) }));
    const other = rows.slice(8).reduce((a, r) => a + val(r), 0);
    if (other > 0) top.push({ name: 'Other', value: Math.round(other * 100) / 100 });
    return top.filter((d) => d.value > 0);
  })();
  const pieSource = rows.some((r) => r.payroll_hours > 0) ? 'Payroll Hours' : 'Job Hours';

  const statCard = (label, value, color, testId) => (
    <div className="rounded-lg p-3" style={{ background: '#1A2433', border: '1px solid #2D3748' }} data-testid={testId}>
      <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>{label}</p>
      <p className="text-lg font-bold" style={{ color }}>{value}</p>
    </div>
  );

  return (
    <>
      <ReportShell
        title={title}
        onExportCSV={() => downloadCSV(`${reportKey}_${startDate}_${endDate}.csv`, rows, summaryCols)}
        onPrint={() => printReport(printRef, title, [
          { label: 'Payroll hours', value: fmtHours(totals.payroll_hours) }, { label: 'Job hours', value: fmtHours(totals.job_hours) },
          { label: 'Variance', value: `${(totals.variance ?? 0).toFixed(2)} h` }])}
        onPdf={() => downloadServerPdf({ token, title, period: `${startDate} → ${endDate}`, columns: summaryCols, rows, chartEl: printRef.current,
          summary: [{ label: 'Payroll hours', value: fmtHours(totals.payroll_hours) }, { label: 'Job hours', value: fmtHours(totals.job_hours) },
            { label: 'Variance', value: `${(totals.variance ?? 0).toFixed(2)} h` }, { label: 'Variance alerts', value: String(totals.alerts ?? 0) }] })}
        onSchedule={() => setScheduleOpen(true)}
        dataTestId={`report-${reportKey}`}
      >
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
          {statCard('Payroll Hours', fmtHours(totals.payroll_hours), '#1E88E5', 'hc-stat-payroll')}
          {statCard('Job Hours', fmtHours(totals.job_hours), '#00C896', 'hc-stat-job')}
          {statCard('Variance', `${(totals.variance ?? 0) > 0 ? '+' : ''}${(totals.variance ?? 0).toFixed(2)} h`, varianceColor(totals.variance), 'hc-stat-variance')}
          {statCard('Variance Alerts', String(totals.alerts ?? 0), (totals.alerts ?? 0) > 0 ? '#EF4444' : '#00C896', 'hc-stat-alerts')}
          {statCard('Est. Labor Cost', fmtMoney(totals.labor_cost), '#2DD4BF', 'hc-stat-cost')}
        </div>

        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 rounded-lg p-1" style={{ background: '#1A2433', border: '1px solid #2D3748' }}>
              {[['bar', 'Bar Chart'], ['pie', 'Pie Chart'], ['none', 'Hide Chart']].map(([k, lbl]) => (
                <button key={k} onClick={() => setChartType(k)}
                        className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                        style={chartType === k ? { background: '#1E88E5', color: 'white' } : { color: '#94A3B8' }}
                        data-testid={`hc-chart-${k}`}>
                  {lbl}
                </button>
              ))}
            </div>
            <select
              value={customerId || ''}
              onChange={(e) => setCustomerId(e.target.value)}
              className="text-xs rounded-lg px-2 py-2"
              style={{ background: '#1A2433', border: '1px solid #2D3748', color: customerId ? 'white' : '#94A3B8' }}
              data-testid="hc-customer-filter"
            >
              <option value="">All customers</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs rounded-lg px-2 py-1.5" style={{ background: '#1A2433', border: '1px solid #2D3748', color: '#94A3B8' }}>
              Alert if variance ≥
              <input
                type="number" min="0" step="0.25"
                value={varThreshold}
                onChange={(e) => setVarThreshold(e.target.value)}
                className="w-14 text-xs rounded px-1 py-0.5 text-white"
                style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                data-testid="hc-variance-threshold"
              />
              h
            </label>
            <button
              onClick={() => setOnlyAlerts((v) => !v)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5"
              style={onlyAlerts
                ? { background: 'rgba(239,68,68,0.15)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.5)' }
                : { background: '#1A2433', color: '#94A3B8', border: '1px solid #2D3748' }}
              data-testid="hc-only-alerts"
            >
              <AlertTriangle className="w-3.5 h-3.5" /> Only mismatches
            </button>
          </div>
          <button
            onClick={() => downloadCSV(`${reportKey}_detailed_${startDate}_${endDate}.csv`, buildDetailedRows(), detailedCols)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5"
            style={{ background: 'rgba(0,200,150,0.12)', color: '#00C896', border: '1px solid rgba(0,200,150,0.4)' }}
            title="One row per worker per group — full payroll vs job-hours breakdown"
            data-testid="hc-export-detailed"
          >
            <Download className="w-3.5 h-3.5" /> Export Detailed CSV (per worker)
          </button>
        </div>

        <div ref={printRef}>
          {chartType !== 'none' && rows.length > 0 && (
            <div className="mb-4" style={{ height: 280 }} data-testid="hc-chart">
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'bar' ? (
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                    <XAxis dataKey="name" tick={{ fill: '#94A3B8', fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={55} />
                    <YAxis tick={{ fill: '#94A3B8', fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: '#1A2433', border: '1px solid #2D3748', borderRadius: 8 }} labelStyle={{ color: 'white' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Payroll Hours" fill="#1E88E5" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Job Hours" fill="#00C896" radius={[3, 3, 0, 0]} />
                  </BarChart>
                ) : (
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {pieData.map((d, i) => <Cell key={d.name} fill={HC_PIE_COLORS[i % HC_PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#1A2433', border: '1px solid #2D3748', borderRadius: 8 }} formatter={(v) => [`${v} h`, pieSource]} />
                  </PieChart>
                )}
              </ResponsiveContainer>
            </div>
          )}

          {rows.length === 0 ? <EmptyState msg={`No ${isProject ? 'project' : 'job'} hours found for this period. Try adjusting the date range.`} /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid #2D3748' }}>
                    <th className="px-2 py-2" style={{ width: 32 }} />
                    {summaryCols.map((c) => (
                      <th key={c.key} className="text-left px-3 py-2 font-medium text-xs uppercase tracking-wider" style={{ color: '#64748B', whiteSpace: 'nowrap' }}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <React.Fragment key={r.id}>
                      <tr style={{ borderBottom: '1px solid #1A2433', cursor: 'pointer' }} onClick={() => toggle(r.id)} data-testid={`hc-row-${r.id}`}>
                        <td className="px-2 py-2">
                          <button className="p-0.5 rounded hover:bg-[#1A2433]" data-testid={`hc-expand-${r.id}`} aria-label="Expand worker breakdown">
                            {expanded.has(r.id) ? <ChevronDown className="w-4 h-4" style={{ color: '#94A3B8' }} /> : <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />}
                          </button>
                        </td>
                        {summaryCols.map((c) => {
                          const target = hcDrill ? (isProject
                            ? (/^(project_number|name)$/.test(c.key) && r.id ? { type: 'project', id: r.id, label: r.name } : null)
                            : (/^(job_number|title)$/.test(c.key) && r.id ? { type: 'job', id: r.id, label: r.title } : null))
                            || (c.key === 'customer_name' && r.customer_id ? { type: 'customer', id: r.customer_id, label: r.customer_name } : null) : null;
                          return (
                            <td key={c.key} className="px-3 py-2 text-white" style={{ whiteSpace: 'nowrap', ...(c.key === 'variance' ? { color: varianceColor(r.variance), fontWeight: 600 } : {}) }}>
                              {target ? (
                                <button type="button" onClick={(e) => { e.stopPropagation(); hcDrill(target); }}
                                        className="text-left text-sky-300 hover:text-sky-200 hover:underline underline-offset-2"
                                        data-testid={`drill-${target.type}-${target.id}`} title={`Open ${target.type} details`}>
                                  {c.render ? c.render(r) : (r[c.key] ?? '-')}
                                </button>
                              ) : (c.render ? c.render(r) : (r[c.key] ?? '-'))}
                            </td>
                          );
                        })}
                      </tr>
                      {expanded.has(r.id) && (
                        <tr style={{ borderBottom: '1px solid #1A2433' }}>
                          <td colSpan={summaryCols.length + 1} className="px-3 pb-3 pt-1">
                            <div className="rounded-lg overflow-hidden" style={{ background: '#0B1321', border: '1px solid #2D3748' }} data-testid={`hc-workers-${r.id}`}>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr style={{ borderBottom: '1px solid #2D3748' }}>
                                    {['Worker', 'Payroll Hours', 'Job Hours', 'Variance', 'Rate', 'Labor Cost'].map((h) => (
                                      <th key={h} className="text-left px-3 py-2 font-medium uppercase tracking-wider" style={{ color: '#64748B' }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {(r.workers || []).map((w) => (
                                    <tr key={w.worker_id} style={{ borderBottom: '1px solid #1A2433' }}>
                                      <td className="px-3 py-2 text-white font-medium">
                                        {hcDrill && w.worker_id ? (
                                          <button type="button" onClick={() => hcDrill({ type: 'worker', id: w.worker_id, label: w.worker_name })}
                                                  className="text-sky-300 hover:underline underline-offset-2" data-testid={`drill-worker-${w.worker_id}`}>{w.worker_name}</button>
                                        ) : w.worker_name}
                                      </td>
                                      <td className="px-3 py-2" style={{ color: '#1E88E5' }}>{fmtHours(w.payroll_hours)}</td>
                                      <td className="px-3 py-2" style={{ color: '#00C896' }}>{fmtHours(w.job_hours)}</td>
                                      <td className="px-3 py-2 font-semibold" style={{ color: varianceColor(w.variance) }}>{w.variance > 0 ? '+' : ''}{(w.variance ?? 0).toFixed(2)} h</td>
                                      <td className="px-3 py-2 text-white">{w.hourly_rate != null ? `$${w.hourly_rate.toFixed(2)}/h` : '—'}</td>
                                      <td className="px-3 py-2" style={{ color: '#2DD4BF' }}>{w.labor_cost != null ? fmtMoney(w.labor_cost) : '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </ReportShell>
      {scheduleOpen && (
        <ScheduleModal
          token={token}
          reportKey={reportKey}
          reportTitle={title}
          onClose={() => setScheduleOpen(false)}
          customerId={customerId}
          extraFilters={{
            ...(customerId ? { customer_id: customerId } : {}),
            ...(varThreshold && !Number.isNaN(parseFloat(varThreshold)) ? { variance_threshold: parseFloat(varThreshold) } : {}),
          }}
        />
      )}
    </>
  );
}

/* ---------- Billable Hours (QuickBooks-Time-style service report, Sep 2026) ---------- */
const BH_COLORS = ['#1E88E5', '#00C896', '#FFA000', '#A78BFA', '#EF4444', '#2DD4BF', '#F472B6', '#7BB8E8',
  '#84CC16', '#F97316', '#38BDF8', '#E879F9', '#FACC15', '#4ADE80', '#FB7185', '#94A3B8'];
const BH_GROUPS = [
  { key: 'job', label: 'Service / Job' },
  { key: 'customer', label: 'Customer' },
  { key: 'label', label: 'Label' },
  { key: 'project', label: 'Project' },
  { key: 'team', label: 'Team' },
  { key: 'area', label: 'Location' },
];

function BillableHoursPane({ title, token, startDate, endDate, printRef, scheduleOpen, setScheduleOpen, customers = [] }) {
  const { isAdmin } = useAuth();
  const [groupBy, setGroupBy] = useState('job');
  const [basis, setBasis] = useState('raw');
  const [intervalSel, setIntervalSel] = useState('auto');
  const [customerId, setCustomerId] = useState('');
  const [chartType, setChartType] = useState('pie');
  const [showAmount, setShowAmount] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setBusy(true); setErr(null);
    axios.get(`${API}/reports/v2/billable-hours`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        start_date: startDate, end_date: endDate, group_by: groupBy, basis,
        interval: intervalSel, ...(customerId ? { customer_id: customerId } : {}),
      },
    }).then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e.response?.data?.detail || 'Failed to load report'); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [token, startDate, endDate, groupBy, basis, intervalSel, customerId]);

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const trend = data?.trend || [];
  const groupLabel = BH_GROUPS.find((g) => g.key === groupBy)?.label || 'Group';

  const pieData = rows.filter((r) => r.hours > 0.005).map((r) => ({ name: r.name, value: r.hours, pct: r.pct }));
  const barData = rows.slice(0, 15).map((r) => ({
    name: r.name.length > 22 ? `${r.name.slice(0, 22)}…` : r.name, Hours: r.hours,
  }));

  const csvCols = [
    { key: 'name', label: groupLabel },
    { key: 'hours', label: `Hours (${basis})` },
    { key: 'pct', label: '% of total' },
    { key: 'entries', label: 'Punches' },
    ...(showAmount ? [{ key: 'amount', label: 'Est. amount ($)' }] : []),
  ];

  const drillCustomer = (r) => {
    if (groupBy !== 'customer' || !r.customer_id) return;
    setCustomerId(r.customer_id);
    setGroupBy('job');
  };

  const pill = (active) => active
    ? { background: '#1E88E5', color: 'white' }
    : { background: 'transparent', color: '#94A3B8' };

  const statCard = (label, value, color, testId) => (
    <div className="rounded-lg p-3" style={{ background: '#1A2433', border: '1px solid #2D3748' }} data-testid={testId}>
      <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>{label}</p>
      <p className="text-lg font-bold" style={{ color }}>{value}</p>
    </div>
  );

  return (
    <>
      <ReportShell
        title={title}
        onExportCSV={() => downloadCSV(`billable_hours_${groupBy}_${startDate}_${endDate}.csv`, rows, csvCols)}
        onPrint={() => printReport(printRef, `${title} — by ${groupLabel}`)}
        onPdf={() => downloadServerPdf({ token, title: `${title} — by ${groupLabel}`, period: `${startDate} → ${endDate}`, columns: csvCols, rows, chartEl: printRef.current })}
        onSchedule={() => setScheduleOpen(true)}
        dataTestId="report-billable-hours"
      >
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid #2D3748' }}>
            {BH_GROUPS.map((g) => (
              <button key={g.key} onClick={() => setGroupBy(g.key)} className="px-3 py-1.5 text-xs font-semibold"
                style={pill(groupBy === g.key)} data-testid={`bh-group-${g.key}`}>
                {g.label}
              </button>
            ))}
          </div>
          <select value={basis} onChange={(e) => setBasis(e.target.value)}
            className="text-xs rounded-md px-2 py-1.5"
            style={{ background: '#1A2433', color: '#CBD5E1', border: '1px solid #2D3748' }}
            data-testid="bh-basis-select">
            <option value="raw">Raw punch hours</option>
            <option value="payroll">Payroll hours (incl. callback min.)</option>
          </select>
          <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}
            className="text-xs rounded-md px-2 py-1.5 max-w-[220px]"
            style={{ background: '#1A2433', color: customerId ? 'white' : '#94A3B8', border: '1px solid #2D3748' }}
            data-testid="bh-customer-select">
            <option value="">All customers</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {isAdmin && (
            <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" style={{ color: '#94A3B8' }}>
              <input type="checkbox" checked={showAmount} onChange={(e) => setShowAmount(e.target.checked)}
                data-testid="bh-amount-toggle" />
              Invoice amount (hours × job rate)
            </label>
          )}
          <div className="ml-auto flex rounded-lg overflow-hidden" style={{ border: '1px solid #2D3748' }}>
            {[['pie', 'Pie'], ['bar', 'Bar'], ['trend', 'Trend']].map(([k, lbl]) => (
              <button key={k} onClick={() => setChartType(k)} className="px-3 py-1.5 text-xs font-semibold"
                style={pill(chartType === k)} data-testid={`bh-chart-${k}`}>
                {lbl}
              </button>
            ))}
          </div>
          {chartType === 'trend' && (
            <select value={intervalSel} onChange={(e) => setIntervalSel(e.target.value)}
              className="text-xs rounded-md px-2 py-1.5"
              style={{ background: '#1A2433', color: '#CBD5E1', border: '1px solid #2D3748' }}
              data-testid="bh-interval-select">
              <option value="auto">Auto</option>
              <option value="day">By day</option>
              <option value="week">By week</option>
              <option value="month">By month</option>
              <option value="year">By year</option>
            </select>
          )}
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          {statCard(`Total hours (${basis})`, fmtHours(totals.hours), '#1E88E5', 'bh-total-hours')}
          {statCard('Job-tagged (billable)', fmtHours(totals.billable_hours), '#00C896', 'bh-billable-hours')}
          {statCard('General (no job tag)', fmtHours(totals.general_hours), totals.general_hours > 0 ? '#FFA000' : '#64748B', 'bh-general-hours')}
          {showAmount
            ? statCard('Est. invoice amount', fmtMoney(totals.amount), '#2DD4BF', 'bh-total-amount')
            : statCard(`${groupLabel}s`, `${totals.buckets ?? 0}`, '#A78BFA', 'bh-bucket-count')}
        </div>
        {totals.open_sessions > 0 && (
          <p className="text-xs mb-3 rounded-md px-3 py-2" style={{ background: 'rgba(255,160,0,0.1)', color: '#FFA000', border: '1px solid rgba(255,160,0,0.35)' }} data-testid="bh-open-warning">
            {totals.open_sessions} open shift{totals.open_sessions === 1 ? '' : 's'} in this range counted up to now — close them before invoicing.
          </p>
        )}
        {groupBy === 'customer' && (
          <p className="text-[11px] mb-2" style={{ color: '#64748B' }}>Click a customer to see its service-item (job) breakdown for the invoice.</p>
        )}

        {err && <p className="text-red-400 text-sm">{err}</p>}
        {busy && !data ? <LoadingSkeleton /> : rows.length === 0 ? (
          <EmptyState msg="No hours in this range. Try a wider date range or clear the customer filter." />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Chart */}
            <div className="rounded-xl p-4" style={{ background: '#0F1923', border: '1px solid #2D3748', minHeight: 340 }} data-testid="bh-chart-panel">
              <ResponsiveContainer width="100%" height={chartType === 'pie' ? 340 : 320}>
                {chartType === 'pie' ? (
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={125}
                      label={(d) => (d.pct >= 3 ? `${d.pct}%` : '')} labelLine={false} isAnimationActive={false}>
                      {pieData.map((_, i) => <Cell key={i} fill={BH_COLORS[i % BH_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#1A2433', border: '1px solid #2D3748', borderRadius: 8 }}
                      formatter={(v, n, p) => [`${v} h (${p?.payload?.pct}%)`, p?.payload?.name]} />
                  </PieChart>
                ) : chartType === 'bar' ? (
                  <BarChart data={barData} layout="vertical" margin={{ left: 30 }}>
                    <XAxis type="number" stroke="#64748B" fontSize={11} />
                    <YAxis type="category" dataKey="name" stroke="#94A3B8" fontSize={10} width={150} />
                    <Tooltip contentStyle={{ background: '#1A2433', border: '1px solid #2D3748', borderRadius: 8 }} />
                    <Bar dataKey="Hours" fill="#1E88E5" radius={[0, 4, 4, 0]} />
                  </BarChart>
                ) : (
                  <BarChart data={trend}>
                    <XAxis dataKey="period" stroke="#64748B" fontSize={11} />
                    <YAxis stroke="#64748B" fontSize={11} />
                    <Tooltip contentStyle={{ background: '#1A2433', border: '1px solid #2D3748', borderRadius: 8 }} formatter={(v) => [`${v} h`, 'Hours']} />
                    <Bar dataKey="hours" fill="#00C896" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>

            {/* Full list with grand total */}
            <div className="rounded-xl overflow-hidden" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
              <div className="max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0" style={{ background: '#121D2E' }}>
                    <tr className="text-left text-xs" style={{ color: '#64748B' }}>
                      <th className="px-3 py-2">{groupLabel}</th>
                      <th className="px-3 py-2 text-right">Hours</th>
                      <th className="px-3 py-2 text-right">%</th>
                      {showAmount && <th className="px-3 py-2 text-right">Est. $</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.key} onClick={() => drillCustomer(r)}
                        className={`border-t border-[#1F2D3F] ${groupBy === 'customer' && r.customer_id ? 'cursor-pointer hover:bg-[#121D2E]' : ''}`}
                        data-testid={`bh-row-${i}`}>
                        <td className="px-3 py-2 text-white">
                          <span className="inline-block w-2.5 h-2.5 rounded-full mr-2" style={{ background: BH_COLORS[i % BH_COLORS.length] }} />
                          {r.name}
                        </td>
                        <td className="px-3 py-2 text-right font-mono" style={{ color: '#CBD5E1' }}>{Number(r.hours).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right" style={{ color: '#94A3B8' }}>{r.pct}%</td>
                        {showAmount && <td className="px-3 py-2 text-right font-mono" style={{ color: '#2DD4BF' }}>{r.amount != null ? fmtMoney(r.amount) : '—'}</td>}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-[#2D3748] font-bold" style={{ background: '#121D2E' }}>
                      <td className="px-3 py-2.5 text-white">Total</td>
                      <td className="px-3 py-2.5 text-right font-mono" style={{ color: '#00C896' }} data-testid="bh-grand-total">
                        {Number(totals.hours ?? 0).toFixed(2)} hrs
                      </td>
                      <td className="px-3 py-2.5 text-right" style={{ color: '#94A3B8' }}>100%</td>
                      {showAmount && <td className="px-3 py-2.5 text-right font-mono" style={{ color: '#2DD4BF' }}>{fmtMoney(totals.amount)}</td>}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}
      </ReportShell>
      {scheduleOpen && (
        <ScheduleModal
          token={token}
          reportKey="billable-hours"
          reportTitle={title}
          onClose={() => setScheduleOpen(false)}
          customerId={customerId}
          extraFilters={{ group_by: groupBy, basis, ...(customerId ? { customer_id: customerId } : {}) }}
        />
      )}
    </>
  );
}

/* ---------- Main report pane — switches on reportKey ---------- */
function ReportPane({ reportKey, token, startDate, endDate, title, runId = 0, onStatus, presetFilters = {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const printRef = useRef(null);
  const chartRef = useRef(null);
  const [chartView, setChartView] = useState('table');
  const [emailSpec, setEmailSpec] = useState(null);
  useEffect(() => { setChartView(presetFilters.chart_view || 'table'); }, [reportKey, presetFilters.chart_view, presetFilters._t]);
  useEffect(() => { brandInfo.chartEl = chartView === 'table' ? null : chartRef.current; });
  useEffect(() => { onStatus?.({ chart_view: chartView }); }, [chartView]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [fixEntry, setFixEntry] = useState(null); // stuck-shift row being closed
  const [fixTime, setFixTime] = useState('');
  const [fixBusy, setFixBusy] = useState(false);
  // Job Margins extras (Jun 2026): by-customer rollup + cost drill-down
  const [marginView, setMarginView] = useState('job');
  const [drill, setDrill] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  // Extras (filters)
  const [metric, setMetric] = useState('jobs_completed');
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [selectedWorker, setSelectedWorker] = useState('');
  const [workers, setWorkers] = useState([]);
  const [trendMonths, setTrendMonths] = useState(6);
  const [heatmapWeeks, setHeatmapWeeks] = useState(12);
  // Safety report filters (Feb 2026)
  const [safetyQuery, setSafetyQuery] = useState('');
  const [safetyWorker, setSafetyWorker] = useState('');
  const [safetyVersion, setSafetyVersion] = useState('');
  // Invoiced Jobs filter (Feb 2026)
  const [invoicedQuery, setInvoicedQuery] = useState('');
  // Hours by Location (Sep 2026)
  const [locQuery, setLocQuery] = useState('');
  const [locExpanded, setLocExpanded] = useState(null);
  const [customerCopy, setCustomerCopy] = useState(false);
  // Travel-vs-On-site Time filters (Feb 2026)
  const [travelQuery, setTravelQuery] = useState('');
  const [travelWorker, setTravelWorker] = useState('');
  const [travelKind, setTravelKind] = useState('');
  const [travelCreatedVia, setTravelCreatedVia] = useState('');
  // Hours-comparison reports (Aug 2026): variance alert threshold, persisted
  const [varThreshold, setVarThreshold] = useState(() => {
    try { return localStorage.getItem('hc_var_threshold') || '1'; } catch { return '1'; }
  });
  useEffect(() => {
    try { localStorage.setItem('hc_var_threshold', varThreshold); } catch { /* noop */ }
  }, [varThreshold]);
  // Payroll Workbench server paging (Sep 2026): one page of workers per request.
  // Page + search are remembered per admin when Company Defaults allow it.
  const { user: paneUser } = useAuth();
  const paneDefaults = readCompanyDefaults();
  const rememberPw = reportKey === 'payroll' && !!paneDefaults.payroll_remember_view;
  const savedPw = rememberPw ? loadPayrollView(paneUser?.id) : {};
  const [pwPage, setPwPage] = useState(savedPw.page || 1);
  const [pwQuery, setPwQuery] = useState(savedPw.query || '');
  const pwPageSize = [25, 50, 100].includes(paneDefaults.payroll_page_size) ? paneDefaults.payroll_page_size : 50;
  const pwFirstRunRef = useRef(true);
  useEffect(() => {
    if (pwFirstRunRef.current) { pwFirstRunRef.current = false; return; }
    setPwPage(1);
  }, [pwQuery, startDate, endDate]);
  useEffect(() => {
    if (rememberPw) savePayrollView(paneUser?.id, { page: pwPage, query: pwQuery });
  }, [rememberPw, pwPage, pwQuery, paneUser?.id]);
  const resetPayrollView = () => {
    clearPayrollView(paneUser?.id);
    setPwQuery('');
    setPwPage(1);
  };
  const fetchAllPayrollRows = useCallback(async () => {
    const res = await axios.get(`${API}/reports/v2/payroll`, {
      headers: { Authorization: `Bearer ${token}` }, params: { start_date: startDate, end_date: endDate },
    });
    return res.data?.rows || [];
  }, [token, startDate, endDate]);

  const reqSeq = useRef(0);
  const fetchData = useCallback(async () => {
    const seq = ++reqSeq.current;
    const fresh = () => seq === reqSeq.current;
    setLoading(true); setErr(null);
    try {
      if (reportKey === 'billable-hours' || reportKey === 'service-rate' || reportKey === 'hours-review') { setData({}); setLoading(false); return; }
      const params = {};
      // Reports that need a range
      const noRangeReports = ['job-status-snapshot', 'overdue-jobs', 'unbilled-jobs', 'monthly-trend',
        'customer-job-history', 'paused-jobs', 'team-attendance-heatmap', 'worker-trend', 'tenure-turnover',
        'stuck-shifts'];
      if (!noRangeReports.includes(reportKey)) { params.start_date = startDate; params.end_date = endDate; }
      if (reportKey === 'attendance-individual') {
        if (!selectedWorker) { setData({ rows: [] }); setLoading(false); onStatus?.({ loading: false, at: Date.now(), rows: 0 }); return; }
        params.user_id = selectedWorker;
      }
      if (reportKey === 'attendance' && selectedWorker) params.user_id = selectedWorker;
      if (reportKey === 'top-performers') params.metric = metric;
      if (reportKey === 'customer-job-history') {
        if (!customerId) { setData({ rows: [] }); setLoading(false); return; }
        params.customer_id = customerId;
      }
      if (reportKey === 'worker-trend') {
        if (!selectedWorker) { setData({ rows: [] }); setLoading(false); return; }
        params.user_id = selectedWorker;
        params.months = trendMonths;
      }
      if (reportKey === 'team-attendance-heatmap') {
        params.weeks = heatmapWeeks;
      }
      if (reportKey === 'safety-attestations') {
        // Server-side filters. Free-text + worker + version. Date range
        // is already in params via the standard range handling above.
        if (safetyQuery.trim()) params.q = safetyQuery.trim();
        if (safetyWorker) params.worker_id = safetyWorker;
        if (safetyVersion) params.version = parseInt(safetyVersion, 10);
      }
      if (reportKey === 'invoiced-jobs') {
        if (invoicedQuery.trim()) params.q = invoicedQuery.trim();
      }
      if (reportKey === 'travel-time') {
        if (travelQuery.trim()) params.q = travelQuery.trim();
        if (travelWorker) params.worker_id = travelWorker;
        if (travelKind) params.kind = travelKind;
        if (travelCreatedVia) params.created_via = travelCreatedVia;
      }
      if (reportKey === 'hours-by-location') {
        if (customerId) params.customer_id = customerId;
        if (locQuery.trim()) params.q = locQuery.trim();
      }
      if (reportKey === 'hours-by-project' || reportKey === 'hours-by-job') {
        if (customerId) params.customer_id = customerId;
        const vt = parseFloat(varThreshold);
        if (!Number.isNaN(vt) && vt > 0) params.variance_threshold = vt;
      }
      if (reportKey === 'payroll') {
        params.page = pwPage;
        params.page_size = pwPageSize;
        if (pwQuery.trim()) params.q = pwQuery.trim();
      }
      onStatus?.({ loading: true });
      const res = await axios.get(`${API}/reports/v2/${reportKey === 'attendance-individual' ? 'attendance' : reportKey}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      if (!fresh()) return;
      setData(res.data);
      const d = res.data || {};
      const n = Array.isArray(d.rows) ? d.rows.length : Array.isArray(d) ? d.length : (d.total ?? d.summary?.total_rows ?? null);
      onStatus?.({ loading: false, at: Date.now(), rows: n });
    } catch (e) {
      if (!fresh()) return;
      setErr(e.response?.data?.detail || 'Failed to load report');
      setData(null);
      onStatus?.({ loading: false, at: Date.now(), rows: null });
    } finally {
      if (fresh()) setLoading(false);
    }
  }, [reportKey, token, startDate, endDate, metric, customerId, selectedWorker, trendMonths, heatmapWeeks, safetyQuery, safetyWorker, safetyVersion, invoicedQuery, travelQuery, travelWorker, travelKind, travelCreatedVia, varThreshold, pwPage, pwQuery, pwPageSize, runId, locQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // Team Attendance Report always opens on "All workers" unless a drill/deep-link preselects one.
  useEffect(() => { if (reportKey === 'attendance' && !presetFilters.worker) setSelectedWorker(''); }, [reportKey]);
  // Drill-down "open report" links can pre-select a worker / customer filter.
  useEffect(() => {
    if (presetFilters.worker) setSelectedWorker(presetFilters.worker);
    if (presetFilters.customer) setCustomerId(presetFilters.customer);
    if (presetFilters.location !== undefined) setLocQuery(presetFilters.location || '');
    if (presetFilters.q !== undefined) { setPwQuery(presetFilters.q || ''); setPwPage(1); }
    if (presetFilters._clear) { setSelectedWorker(''); setCustomerId(''); setLocQuery(''); setPwQuery(''); }
  }, [presetFilters.worker, presetFilters.customer, presetFilters.location, presetFilters._t]);
  const periodLabel = startDate && endDate && !NO_RANGE_REPORTS.includes(reportKey) ? `${startDate} → ${endDate}` : '';
  const pdfSpec = (pdfRows, pdfCols, pdfTitle, summary) => ({
    title: pdfTitle || title, period: periodLabel, columns: pdfCols, rows: pdfRows, summary: summary || [],
    chartEl: chartView === 'table' ? null : chartRef.current,
  });
  const serverPdf = (pdfRows, pdfCols, pdfTitle, summary) => downloadServerPdf({ token, ...pdfSpec(pdfRows, pdfCols, pdfTitle, summary) });
  const emailPdf = (pdfRows, pdfCols, pdfTitle, summary) => setEmailSpec(pdfSpec(pdfRows, pdfCols, pdfTitle, summary));
  useEffect(() => { onStatus?.({ filters: { ...(selectedWorker ? { worker: selectedWorker } : {}), ...(customerId ? { customer: customerId } : {}) } }); }, [selectedWorker, customerId]);

  // Payroll Workbench: swap one worker's row in place after an edit so the
  // table (and the admin's scroll position) is left untouched.
  const patchPayrollRow = useCallback((workerId, row) => {
    setData((d) => {
      if (!d?.rows) return d;
      const old = d.rows.find((r) => r.worker_id === workerId);
      if (!old) return d;
      const rows = row ? d.rows.map((r) => (r.worker_id === workerId ? row : r)) : d.rows.filter((r) => r.worker_id !== workerId);
      let summary = d.summary;
      if (summary) {
        const nxt = row || {};
        const r2 = (v) => Math.round(v * 100) / 100;
        summary = {
          ...summary,
          total_hours: r2((summary.total_hours || 0) - (old.total_hours || 0) + (nxt.total_hours || 0)),
          payroll_hours: r2((summary.payroll_hours || 0) - (old.payroll_hours || 0) + (nxt.payroll_hours || 0)),
          overtime_hours: r2((summary.overtime_hours || 0) - (old.overtime_hours || 0) + (nxt.overtime_hours || 0)),
          anomalies: Math.max(0, (summary.anomalies || 0) - (old.anomalies?.length || 0) + (nxt.anomalies?.length || 0)),
          gross_pay: r2((summary.gross_pay || 0) - (old.gross_pay || 0) + (nxt.gross_pay || 0)),
        };
      }
      return { ...d, rows, summary };
    });
  }, []);

  // Load customer list for customer-history report
  useEffect(() => {
    if (['customer-job-history', 'hours-by-project', 'hours-by-job', 'billable-hours', 'hours-by-location'].includes(reportKey)) {
      axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setCustomers(r.data || []))
        .catch(() => setCustomers([]));
    }
  }, [reportKey, token]);

  // Load workers list for worker-trend
  useEffect(() => {
    if (reportKey === 'worker-trend' || reportKey === 'safety-attestations' || reportKey === 'travel-time' || reportKey === 'attendance' || reportKey === 'attendance-individual') {
      axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setWorkers(r.data || []))
        .catch(() => setWorkers([]));
    }
  }, [reportKey, token]);

  if (loading && !data) return (
    <>
      <ReportShell title={title} dataTestId={`report-${reportKey}`}><LoadingSkeleton /></ReportShell>
      {scheduleOpen && <ScheduleModal token={token} reportKey={reportKey} reportTitle={title} onClose={() => setScheduleOpen(false)} customerId={customerId} />}
    </>
  );
  if (err) return (
    <>
      <ReportShell title={title} dataTestId={`report-${reportKey}`}><p className="text-red-400 text-sm">{err}</p></ReportShell>
      {scheduleOpen && <ScheduleModal token={token} reportKey={reportKey} reportTitle={title} onClose={() => setScheduleOpen(false)} customerId={customerId} />}
    </>
  );

  // Render per report
  const rows = data?.rows || [];

  const commonShell = (columns, renderExtras, chartNode, customRows, emptyMsg, belowNode = null, opts = {}) => {
    const tableRows = customRows || rows;
    return (
    <>
      <ReportShell
        title={title}
        onExportCSV={() => downloadCSV(opts.csvName || `${reportKey}_${startDate || 'all'}_${endDate || 'all'}.csv`, tableRows, columns, opts.csvMeta || [], opts.printTitle || title)}
        onPrint={() => printReport(printRef, opts.printTitle || title, opts.printSummary)}
        onPdf={() => serverPdf(tableRows, columns, opts.printTitle, opts.printSummary)}
        onEmail={() => emailPdf(tableRows, columns, opts.printTitle, opts.printSummary)}
        onSchedule={() => setScheduleOpen(true)}
        dataTestId={`report-${reportKey}`}
      >
        {emailSpec && <EmailPdfDialog open onClose={() => setEmailSpec(null)} token={token} spec={emailSpec} />}
        {renderExtras}
        {tableRows.length > 0 && <ChartSwitcher rows={tableRows} columns={columns} view={chartView} onView={setChartView} chartRef={chartRef} />}
        <div ref={printRef}>
          {chartNode}
          {tableRows.length === 0 ? <EmptyState msg={emptyMsg || undefined} /> : <DataTable rows={tableRows} columns={columns} />}
          {belowNode}
        </div>
      </ReportShell>
      {scheduleOpen && <ScheduleModal token={token} reportKey={opts.scheduleKey || reportKey} reportTitle={opts.printTitle || title} onClose={() => setScheduleOpen(false)} customerId={customerId} extraFilters={opts.scheduleFilters} />}
    </>
    );
  };

  /* ───── No-Shows & Absences (Aug 2026) ───── */
  if (reportKey === 'no-shows') {
    const NS_STYLES = {
      no_show: { color: '#EF4444', bg: 'rgba(239,68,68,0.12)', label: 'No-show' },
      vacation: { color: '#1E88E5', bg: 'rgba(30,136,229,0.12)', label: 'Vacation' },
      sick: { color: '#FFA000', bg: 'rgba(255,160,0,0.12)', label: 'Sick' },
      other: { color: '#94A3B8', bg: 'rgba(148,163,184,0.12)', label: 'Other' },
    };
    const cols = [
      { key: 'date', label: 'Date' },
      { key: 'worker_name', label: 'Worker' },
      { key: 'team_name', label: 'Team' },
      { key: 'shift_title', label: 'Shift', render: (r) => r.shift_title || '—' },
      { key: 'shift_time', label: 'Scheduled', render: (r) => r.shift_time || '—' },
      { key: 'status', label: 'Status', render: (r) => {
        const st = NS_STYLES[r.status] || NS_STYLES.other;
        return (
          <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ color: st.color, background: st.bg }} data-testid={`ns-status-${r.worker_id}-${r.date}`}>
            {st.label}
          </span>
        );
      } },
      { key: 'tagged_by', label: 'Tagged By', render: (r) => r.tagged_by || '—' },
    ];
    const nsTotals = data?.totals || {};
    const extras = (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
        <div className="rounded-lg p-3" style={{ background: '#1A2433', border: '1px solid #2D3748' }} data-testid="ns-stat-noshows">
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>Untagged No-Shows</p>
          <p className="text-lg font-bold" style={{ color: (nsTotals.no_shows || 0) > 0 ? '#EF4444' : '#00C896' }}>{nsTotals.no_shows ?? 0}</p>
        </div>
        <div className="rounded-lg p-3" style={{ background: '#1A2433', border: '1px solid #2D3748' }} data-testid="ns-stat-tagged">
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>Tagged Absences</p>
          <p className="text-lg font-bold" style={{ color: '#FFA000' }}>{nsTotals.tagged ?? 0}</p>
        </div>
        <div className="rounded-lg p-3 col-span-2 md:col-span-1" style={{ background: '#1A2433', border: '1px solid #2D3748' }}>
          <p className="text-xs" style={{ color: '#64748B' }}>Rows come from team shift schedules — a worker is a no-show when a scheduled shift started and they never punched in that day. Supervisors tag reasons in the team clock-in roster.</p>
        </div>
      </div>
    );
    return commonShell(cols, extras, null);
  }

  /* ───── Stuck Shifts (Aug 2026) ───── */
  if (reportKey === 'stuck-shifts') {
    const st = data?.totals || {};
    const submitFix = async () => {
      if (!fixEntry) return;
      setFixBusy(true);
      try {
        await axios.post(
          `${API}/admin/time-entries/${fixEntry.entry_id}/force-clockout`,
          { clock_out_at: fixTime ? new Date(fixTime).toISOString() : null },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setFixEntry(null);
        fetchData();
      } catch (err) {
        alert(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Could not close the shift');
      } finally {
        setFixBusy(false);
      }
    };
    const cols = [
      { key: 'worker_name', label: 'Worker' },
      { key: 'clock_in', label: 'Clocked In', render: (r) => new Date(r.clock_in).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) },
      { key: 'elapsed_hours', label: 'On Clock', render: (r) => (
        <span className="font-bold" style={{ color: r.elapsed_hours >= 15 ? '#EF4444' : '#FFA000' }}>{r.elapsed_hours} h</span>
      ) },
      { key: 'job', label: 'Job', render: (r) => r.job || '—' },
      { key: 'source', label: 'Source' },
      { key: 'auto_out_after_h', label: 'Auto-out After', render: (r) => (r.auto_out_after_h ? `${r.auto_out_after_h} h` : 'default') },
      { key: 'fix', label: '', render: (r) => (
        <button
          onClick={() => {
            // default corrected end: clock-in + 8h, capped at now
            const ci = new Date(r.clock_in);
            const guess = new Date(Math.min(ci.getTime() + 8 * 3600000, Date.now()));
            const pad = (n) => String(n).padStart(2, '0');
            setFixTime(`${guess.getFullYear()}-${pad(guess.getMonth() + 1)}-${pad(guess.getDate())}T${pad(guess.getHours())}:${pad(guess.getMinutes())}`);
            setFixEntry(r);
          }}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold"
          style={{ background: 'rgba(30,136,229,0.15)', border: '1px solid rgba(30,136,229,0.5)', color: '#7BB8E8' }}
          data-testid="stuck-fix-btn"
        >
          Close shift
        </button>
      ) },
    ];
    const extras = (
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="rounded-lg p-3" style={{ background: '#1A2433', border: '1px solid #2D3748' }} data-testid="stuck-stat-count">
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>Open Shifts ≥ 10 h</p>
          <p className="text-lg font-bold" style={{ color: (st.stuck || 0) > 0 ? '#EF4444' : '#00C896' }}>{st.stuck ?? 0}</p>
        </div>
        <div className="rounded-lg p-3" style={{ background: '#1A2433', border: '1px solid #2D3748' }} data-testid="stuck-stat-worst">
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>Longest Open Shift</p>
          <p className="text-lg font-bold text-white">{st.worst_hours ? `${st.worst_hours} h` : '—'}</p>
        </div>
      </div>
    );
    return (
      <>
        {commonShell(cols, extras, null, null, 'No shifts have been open for 10+ hours — nobody is stuck on the clock right now.')}
        {fixEntry && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" style={{ background: 'rgba(11,19,33,0.8)', backdropFilter: 'blur(4px)' }} data-testid="stuck-fix-dialog">
            <div className="w-full max-w-sm rounded-2xl p-6" style={{ background: '#1A2433', border: '1px solid #2D3748' }}>
              <h3 className="text-lg font-semibold text-white mb-1">Close stuck shift</h3>
              <p className="text-sm mb-4" style={{ color: '#94A3B8' }}>
                <span className="text-white font-medium">{fixEntry.worker_name}</span> has been on the clock
                since {new Date(fixEntry.clock_in).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.
                Set the corrected end time — the entry is marked as admin-edited for the audit trail.
              </p>
              <label className="block text-xs font-medium mb-1" style={{ color: '#64748B' }}>Corrected end time</label>
              <input
                type="datetime-local"
                value={fixTime}
                onChange={(e) => setFixTime(e.target.value)}
                className="w-full rounded-lg px-3 py-2.5 mb-4 text-sm text-white"
                style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                data-testid="stuck-fix-time-input"
              />
              <div className="flex gap-2">
                <button
                  onClick={submitFix}
                  disabled={fixBusy || !fixTime}
                  className="flex-1 py-2.5 rounded-lg font-semibold text-white disabled:opacity-50"
                  style={{ background: '#1E88E5' }}
                  data-testid="stuck-fix-confirm-btn"
                >
                  {fixBusy ? 'Closing…' : 'Close shift'}
                </button>
                <button
                  onClick={() => setFixEntry(null)}
                  disabled={fixBusy}
                  className="px-4 py-2.5 rounded-lg text-sm font-semibold"
                  style={{ color: '#94A3B8', border: '1px solid #2D3748' }}
                  data-testid="stuck-fix-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  /* ───── QR Scan Activity (Aug 2026) ───── */
  if (reportKey === 'qr-scan-activity') {
    const qt = data?.totals || {};
    const cols = [
      { key: 'job_number', label: 'Job #', render: (r) => r.job_number || '—' },
      { key: 'job_title', label: 'Job' },
      { key: 'has_qr', label: 'QR Poster', render: (r) => (
        <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={r.has_qr
          ? { color: '#00C896', background: 'rgba(0,200,150,0.12)' }
          : { color: '#94A3B8', background: 'rgba(148,163,184,0.12)' }}>
          {r.has_qr ? 'Active' : 'None'}
        </span>
      ) },
      { key: 'qr_scans', label: 'QR Scans', render: (r) => (
        <span className="font-bold" style={{ color: r.qr_scans > 0 ? '#00C896' : '#64748B' }}>{r.qr_scans}</span>
      ) },
      { key: 'skipped_scans', label: 'Skipped (GPS/manual)', render: (r) => (
        r.has_qr
          ? <span className="font-semibold" style={{ color: r.skipped_scans > 0 ? '#FFA000' : '#64748B' }}>{r.skipped_scans}</span>
          : '—'
      ) },
      { key: 'scan_rate', label: 'Scan Rate', render: (r) => (r.scan_rate != null ? `${r.scan_rate}%` : '—') },
      { key: 'last_scan_at', label: 'Last Scan', render: (r) => (
        r.last_scan_at
          ? new Date(r.last_scan_at).toLocaleDateString()
          : (r.has_qr ? <span style={{ color: '#EF4444' }} className="font-semibold">Never</span> : '—')
      ) },
    ];
    const extras = (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div className="rounded-lg p-3" style={{ background: '#1A2433', border: '1px solid #2D3748' }} data-testid="qr-stat-jobs">
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>Jobs with QR</p>
          <p className="text-lg font-bold text-white">{qt.jobs_with_qr ?? 0}</p>
        </div>
        <div className="rounded-lg p-3" style={{ background: '#1A2433', border: '1px solid #2D3748' }} data-testid="qr-stat-scans">
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>Total QR Scans</p>
          <p className="text-lg font-bold" style={{ color: '#00C896' }}>{qt.total_qr_scans ?? 0}</p>
        </div>
        <div className="rounded-lg p-3" style={{ background: '#1A2433', border: '1px solid #2D3748' }} data-testid="qr-stat-never">
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>Posters Never Scanned</p>
          <p className="text-lg font-bold" style={{ color: (qt.never_scanned || 0) > 0 ? '#EF4444' : '#00C896' }}>{qt.never_scanned ?? 0}</p>
        </div>
        <div className="rounded-lg p-3" style={{ background: '#1A2433', border: '1px solid #2D3748' }} data-testid="qr-stat-skipped">
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>Punches Skipping Scan</p>
          <p className="text-lg font-bold" style={{ color: (qt.skipped_scans || 0) > 0 ? '#FFA000' : '#00C896' }}>{qt.skipped_scans ?? 0}</p>
        </div>
      </div>
    );
    return commonShell(cols, extras, null);
  }

  /* ───── Hours Review (every entry · regular / OT / call-out top-up · by job vs project) ───── */
  if (reportKey === 'hours-review') {
    return <HoursReviewPane token={token} startDate={startDate} endDate={endDate} />;
  }

  /* ───── Service Rate Calculator (hours + monthly costs → what to charge) ───── */
  if (reportKey === 'service-rate') {
    return <ServiceRatePane token={token} startDate={startDate} />;
  }

  /* ───── Billable Hours (service-item / customer invoicing report) ───── */
  if (reportKey === 'billable-hours') {
    return (
      <BillableHoursPane
        title={title}
        token={token}
        startDate={startDate}
        endDate={endDate}
        printRef={printRef}
        scheduleOpen={scheduleOpen}
        setScheduleOpen={setScheduleOpen}
        customers={customers}
      />
    );
  }

  /* ───── Hours by Project / Hours by Job (payroll vs billing comparison) ───── */
  if (reportKey === 'hours-by-project' || reportKey === 'hours-by-job') {
    return (
      <HoursComparisonPane
        reportKey={reportKey}
        title={title}
        data={data}
        startDate={startDate}
        endDate={endDate}
        printRef={printRef}
        scheduleOpen={scheduleOpen}
        setScheduleOpen={setScheduleOpen}
        token={token}
        customers={customers}
        customerId={customerId}
        setCustomerId={setCustomerId}
        varThreshold={varThreshold}
        setVarThreshold={setVarThreshold}
      />
    );
  }

  /* ───── Job Margins (Jun 2026 — Phase 5) ───── */
  if (reportKey === 'job-margins') {
    const s = data?.summary || {};
    const money = (v) => `$${(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const marginColor = (m) => ((m ?? 0) < 0 ? '#EF4444' : (m ?? 0) === 0 ? '#A0AEC0' : '#00C896');
    const cols = [
      { key: 'job_number', label: 'Job #' },
      { key: 'title', label: 'Job' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'status', label: 'Status', render: (r) => (r.status || '').replace('_', ' ') },
      { key: 'hours', label: 'Hours', render: (r) => fmtHours(r.hours) },
      { key: 'revenue', label: 'Revenue', render: (r) => (
        <span>{money(r.revenue)} <span style={{ fontSize: 10, color: '#64748B' }}>({r.revenue_source})</span></span>
      ) },
      { key: 'labor_cost', label: 'Labor', render: (r) => money(r.labor_cost) },
      { key: 'material_cost', label: 'Materials', render: (r) => money(r.material_cost) },
      { key: 'margin', label: 'Margin', render: (r) => <b style={{ color: marginColor(r.margin) }}>{money(r.margin)}</b>,
        cellStyle: (r) => (r.margin < 0 ? { background: 'rgba(239,68,68,0.06)' } : {}) },
      { key: 'margin_pct', label: 'Margin %', render: (r) => (r.margin_pct == null ? '—' : <span style={{ color: marginColor(r.margin) }}>{r.margin_pct}%</span>) },
      { key: 'invoiced', label: 'Invoiced', render: (r) => (r.invoiced ? <span style={{ color: '#00C896' }}>✓</span> : '') },
      { key: 'details', label: '', render: (r) => (
        <button
          onClick={() => openDrill(r)}
          className="px-2 py-0.5 rounded text-xs font-semibold"
          style={{ color: '#1E88E5', border: '1px solid rgba(30,136,229,0.4)' }}
          data-testid={`margin-drill-${r.job_id}`}
        >Details</button>
      ) },
    ];
    const openDrill = async (r) => {
      setDrill({ row: r, detail: null });
      setDrillLoading(true);
      try {
        const res = await axios.get(`${API}/reports/v2/job-margins/${r.job_id}/detail`, { headers: { Authorization: `Bearer ${token}` } });
        setDrill({ row: r, detail: res.data });
      } catch (e) {
        setDrill({ row: r, detail: { error: e.response?.data?.detail || 'Could not load breakdown' } });
      } finally { setDrillLoading(false); }
    };
    // By-customer rollup (client-side)
    const custMap = {};
    rows.forEach((r) => {
      const k = r.customer_name || 'No customer';
      const c = custMap[k] || (custMap[k] = { customer_name: k, jobs: 0, hours: 0, revenue: 0, total_cost: 0 });
      c.jobs += 1; c.hours += r.hours; c.revenue += r.revenue; c.total_cost += r.total_cost;
    });
    const custRows = Object.values(custMap).map((c) => ({
      ...c,
      hours: Math.round(c.hours * 100) / 100,
      revenue: Math.round(c.revenue * 100) / 100,
      total_cost: Math.round(c.total_cost * 100) / 100,
      margin: Math.round((c.revenue - c.total_cost) * 100) / 100,
      margin_pct: c.revenue > 0 ? Math.round((c.revenue - c.total_cost) / c.revenue * 1000) / 10 : null,
    })).sort((a, b) => a.margin - b.margin);
    const custCols = [
      { key: 'customer_name', label: 'Customer' },
      { key: 'jobs', label: 'Jobs' },
      { key: 'hours', label: 'Hours', render: (r) => fmtHours(r.hours) },
      { key: 'revenue', label: 'Revenue', render: (r) => money(r.revenue) },
      { key: 'total_cost', label: 'Cost', render: (r) => money(r.total_cost) },
      { key: 'margin', label: 'Margin', render: (r) => <b style={{ color: marginColor(r.margin) }}>{money(r.margin)}</b>,
        cellStyle: (r) => (r.margin < 0 ? { background: 'rgba(239,68,68,0.06)' } : {}) },
      { key: 'margin_pct', label: 'Margin %', render: (r) => (r.margin_pct == null ? '—' : <span style={{ color: marginColor(r.margin) }}>{r.margin_pct}%</span>) },
    ];
    const extras = (
      <>
      <div className="flex gap-1 mb-3" data-testid="margin-view-toggle">
        {[['job', 'By Job'], ['customer', 'By Customer']].map(([v, l]) => (
          <button key={v} onClick={() => setMarginView(v)}
            className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
            style={marginView === v
              ? { background: '#1E88E5', color: '#fff' }
              : { background: '#1A2433', color: '#A0AEC0', border: '1px solid #2D3748' }}
            data-testid={`margin-view-${v}`}
          >{l}</button>
        ))}
      </div>
      <p className="mb-3 text-xs" style={{ color: '#64748B' }} data-testid="job-margins-scope-note">
        Margins are <b>job-lifetime totals</b> (what you'd invoice vs what the whole job cost). The date range picks which jobs appear: jobs with activity or completed in range, plus everything currently in progress.
      </p>
      {(data?.trend || []).some((t) => t.revenue > 0 || t.cost > 0) && (
        <div className="mb-4" data-testid="job-margins-trend">
          <p className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: '#64748B' }}>Margin % — last 6 months (completed jobs)</p>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.trend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                <XAxis dataKey="month" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} unit="%" />
                <Tooltip
                  contentStyle={{ background: '#1A2433', border: '1px solid #2D3748', borderRadius: 8, fontSize: 12 }}
                  formatter={(v, name) => (name === 'Margin %' ? [`${v ?? '—'}%`, name] : [`$${(v ?? 0).toLocaleString()}`, name])}
                />
                <Line type="monotone" dataKey="margin_pct" name="Margin %" stroke="#00C896" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {data?.rates_configured === false && (
        <div className="mb-3 p-3 rounded-lg text-xs" style={{ background: 'rgba(255,160,0,0.08)', border: '1px solid rgba(255,160,0,0.4)', color: '#FFA000' }} data-testid="job-margins-rates-hint">
          No worker hourly rates are set, so labor cost shows $0. Set each worker's hourly rate in <b>Staff → edit worker</b> to see true margins.
        </div>
      )}
      <div className="flex flex-wrap gap-3 mb-4" data-testid="job-margins-summary">
        {[
          { l: 'Jobs', v: s.jobs ?? 0, c: '#A0AEC0' },
          { l: 'Revenue', v: money(s.revenue), c: '#1E88E5' },
          { l: 'Cost', v: money(s.cost), c: '#FFA000' },
          { l: 'Margin', v: money(s.margin), c: marginColor(s.margin) },
          { l: 'Margin %', v: s.margin_pct == null ? '—' : `${s.margin_pct}%`, c: marginColor(s.margin) },
        ].map((chip) => (
          <div key={chip.l} className="px-4 py-2 rounded-lg" style={{ background: '#1A2433', border: '1px solid #2D3748' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#64748B' }}>{chip.l}</p>
            <p className="text-lg font-bold" style={{ color: chip.c }}>{chip.v}</p>
          </div>
        ))}
      </div>
      </>
    );
    const dd = drill?.detail;
    return (
      <>
        {commonShell(marginView === 'customer' ? custCols : cols, extras, null, marginView === 'customer' ? custRows : null)}
        <MarginDrillDialog drill={drill} loading={drillLoading} onClose={() => setDrill(null)} money={money} marginColor={marginColor} dd={dd} />
      </>
    );
  }

  /* ───── 1) Attendance (team) + Individual Attendance ───── */
  if (reportKey === 'attendance' || reportKey === 'attendance-individual') {
    const individual = reportKey === 'attendance-individual';
    const worker = data?.worker || workers.find((w) => w.id === selectedWorker) || null;
    const summary = data?.summary || null;
    // One line per punch (worker's own timezone) + a bold day-total line when a day has several punches.
    const styled = rows.map((r) => r.row_type === 'total' ? { ...r, _rowStyle: { background: 'rgba(30,136,229,0.08)', fontWeight: 600 } }
      : r.row_type === 'absent' ? { ...r, _rowStyle: { background: 'rgba(239,68,68,0.04)' } } : r);
    const statusColor = (s) => s === 'Absent' ? '#EF4444' : (s === 'Auto Clocked Out' || s === 'Still Clocked In') ? '#FFA000' : '#00C896';
    const workerCell = (r) => {
      if (r.row_type === 'total') return '';
      if (selectedWorker) return r.worker_name;
      return (
        <button type="button" onClick={() => setSelectedWorker(r.worker_id)} className="underline decoration-dotted underline-offset-2 hover:text-white text-left"
                title={`Show only ${r.worker_name}`} data-testid={`attendance-worker-link-${r.worker_id}`}>{r.worker_name}</button>
      );
    };
    const cols = [
      ...(individual ? [] : [{ key: 'worker_name', label: 'Worker', noDrill: true, render: workerCell, value: (r) => r.worker_name }]),
      { key: 'date', label: 'Date', render: (r) => fmtDate(r.date), value: (r) => r.date },
      { key: 'clock_in_local', label: 'Clock-In', render: (r) => r.overlap && r.row_type === 'session' ? `${r.clock_in_local} ⚠` : (r.clock_in_local || '—'), value: (r) => r.clock_in_local || '—',
        cellStyle: (r) => r.overlap && r.row_type === 'session' ? { color: '#FFA000' } : {} },
      { key: 'clock_out_local', label: 'Clock-Out', render: (r) => r.clock_out_local || '—' },
      { key: 'hours', label: 'Duration', render: (r) => r.hours == null ? (r.row_type === 'absent' ? '0.00 hrs' : '—') : `${Number(r.hours).toFixed(2)} hrs`,
        cellStyle: () => ({ textAlign: 'right' }) },
      { key: 'work', label: 'Project / Job', render: (r) => r.work || (r.row_type === 'absent' ? '—' : ''), value: (r) => r.work || '' },
      { key: 'hours_allocated', label: 'Hours Allocated', render: (r) => r.row_type === 'total' ? '' : r.hours_allocated == null ? '—' : `${Number(r.hours_allocated).toFixed(2)}${r.job_hours_to_date != null ? ` (${Number(r.job_hours_to_date).toFixed(2)} used)` : ''}`,
        value: (r) => r.hours_allocated == null ? '' : Number(r.hours_allocated).toFixed(2), cellStyle: () => ({ textAlign: 'right' }) },
      { key: 'total_hours', label: 'Day Total', render: (r) => r.total_hours == null ? '' : `${Number(r.total_hours).toFixed(2)} hrs`, cellStyle: () => ({ textAlign: 'right' }) },
      { key: 'status', label: 'Status', render: (r) => r.status ? <span style={{ color: statusColor(r.status), fontWeight: 600 }}>{r.status}</span> : '', value: (r) => r.status || '' },
      { key: 'timezone', label: 'Time Zone', render: (r) => r.timezone || '' },
    ];
    const picker = (
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="attendance-worker-filter">
        <label className="text-xs" style={{ color: individual ? '#E2E8F0' : '#A0AEC0' }} htmlFor="attendance-worker-picker">
          {individual ? 'Worker' : 'Show'}
        </label>
        <select id="attendance-worker-picker" value={selectedWorker} onChange={(e) => setSelectedWorker(e.target.value)}
                className="px-2 py-1.5 rounded-lg text-xs min-w-[240px]" style={{ background: '#1A2433', color: '#fff', border: `1px solid ${individual && !selectedWorker ? '#1E88E5' : '#2D3748'}` }}
                data-testid="attendance-worker-picker">
          <option value="">{individual ? 'Select a worker…' : 'All workers (whole team)'}</option>
          {workers.map((w) => <option key={w.id} value={w.id}>{w.name}{w.role && w.role !== 'staff' ? ` (${w.role})` : ''}</option>)}
        </select>
        {selectedWorker && !individual && (
          <button type="button" onClick={() => setSelectedWorker('')} className="text-xs px-2 py-1 rounded-md" style={{ color: '#93C5FD', border: '1px solid #2D3748' }} data-testid="attendance-worker-clear">
            Show everyone
          </button>
        )}
        {!individual && !selectedWorker && (
          <span className="text-[11px]" style={{ color: '#64748B' }}>Tip: click a worker's name in the table for their individual report.</span>
        )}
      </div>
    );
    const stat = (label, value, testId, color) => (
      <div className="rounded-lg px-3 py-2" style={{ background: '#0B1321', border: '1px solid #2D3748', minWidth: 120 }} data-testid={testId}>
        <div className="text-[10px] uppercase tracking-wider" style={{ color: '#64748B' }}>{label}</div>
        <div className="text-base font-semibold" style={{ color: color || '#fff' }}>{value}</div>
      </div>
    );
    const summaryCard = worker && summary ? (
      <div className="rounded-xl p-3 mb-3" style={{ background: 'rgba(30,136,229,0.08)', border: '1px solid rgba(30,136,229,0.35)' }} data-testid="attendance-individual-summary">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
          <div>
            <div className="text-sm font-semibold text-white" data-testid="attendance-individual-name">{worker.name}</div>
            <div className="text-[11px]" style={{ color: '#A0AEC0' }}>{worker.email || ''}{worker.timezone ? ` · ${describeTzSpec(worker.timezone)}` : ''} · {startDate} → {endDate}</div>
          </div>
          <div className="text-[11px]" style={{ color: '#64748B' }}>Individual attendance report</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {stat('Days present', summary.days_present, 'att-sum-days')}
          {stat('Total hours', `${Number(summary.total_hours).toFixed(2)} hrs`, 'att-sum-hours', '#00C896')}
          {stat('Avg / day', `${Number(summary.avg_hours_per_day).toFixed(2)} hrs`, 'att-sum-avg')}
          {stat('Punches', summary.sessions, 'att-sum-sessions')}
          {stat('Absent days', summary.absent_days, 'att-sum-absent', summary.absent_days ? '#EF4444' : undefined)}
          {stat('Flagged punches', summary.flagged_punches, 'att-sum-flagged', summary.flagged_punches ? '#FFA000' : undefined)}
        </div>
        {summary.by_work?.length > 0 && (
          <div className="mt-2 text-[11px]" style={{ color: '#CBD5E1' }} data-testid="att-sum-by-work">
            <span style={{ color: '#64748B' }}>Hours by project / job: </span>
            {summary.by_work.slice(0, 6).map((w, i) => <span key={w.work}>{i > 0 ? ' · ' : ''}{w.work} <b>{Number(w.hours).toFixed(2)}</b></span>)}
            {summary.by_work.length > 6 && <span> · +{summary.by_work.length - 6} more</span>}
          </div>
        )}
      </div>
    ) : null;
    const note = (
      <>
        {picker}
        {summaryCard}
        <p className="text-[11px] mb-2" style={{ color: '#64748B' }} data-testid="attendance-tz-note">
          Clock-in / clock-out times are shown in each worker's own time zone and match their time history. CSV exports use the same readable format.
        </p>
      </>
    );
    const slug = worker?.name ? worker.name.replace(/[^a-z0-9]+/gi, '_') : '';
    const printSummary = summary ? [
      { label: 'Worker', value: worker?.name || '' }, { label: 'Days present', value: String(summary.days_present) },
      { label: 'Total hours', value: `${Number(summary.total_hours).toFixed(2)} h` }, { label: 'Absent days', value: String(summary.absent_days) }] : undefined;
    const emptyMsg = individual && !selectedWorker ? 'Select a worker above to produce their individual attendance report.'
      : selectedWorker ? 'No attendance activity for this worker in the selected period.' : undefined;
    return commonShell(cols, note, null, styled, emptyMsg, null, {
      csvName: selectedWorker ? `attendance_${slug || selectedWorker}_${startDate}_${endDate}.csv` : undefined,
      csvMeta: worker ? [['Worker', worker.name], ['Email', worker.email || ''], ['Time zone', worker.timezone ? describeTzSpec(worker.timezone) : ''],
        ...(summary ? [['Days present', summary.days_present], ['Total hours', Number(summary.total_hours).toFixed(2)], ['Absent days', summary.absent_days]] : [])] : [],
      printTitle: worker ? `Attendance Report — ${worker.name}` : undefined,
      printSummary,
      scheduleKey: 'attendance',
      scheduleFilters: selectedWorker ? { user_id: selectedWorker } : undefined,
    });
  }

  /* ───── 2) Worker Performance ───── */
  if (reportKey === 'worker-performance') {
    const badgeColor = (b) => b === 'Excellent' ? '#00C896' : b === 'Good' ? '#1E88E5' : '#EF4444';
    const cols = [
      { key: 'worker_name', label: 'Worker' },
      { key: 'jobs_assigned', label: 'Assigned' },
      { key: 'jobs_accepted', label: 'Accepted' },
      { key: 'jobs_completed', label: 'Completed' },
      { key: 'acceptance_rate', label: 'Accept %', render: (r) => `${r.acceptance_rate}%` },
      { key: 'on_time_rate', label: 'On-Time %', render: (r) => `${r.on_time_rate}%` },
      { key: 'avg_hours_per_job', label: 'Avg h/Job', render: (r) => fmtHours(r.avg_hours_per_job) },
      { key: 'badge', label: 'Badge', render: (r) => (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                style={{ background: `${badgeColor(r.badge)}20`, color: badgeColor(r.badge), border: `1px solid ${badgeColor(r.badge)}` }}>
            {r.badge}
          </span>
      )},
    ];
    return commonShell(cols, null, null);
  }

  /* ───── 3) Staff Hours Summary ───── */
  if (reportKey === 'staff-hours-summary') {
    const cols = [
      { key: 'worker_name', label: 'Worker' },
      { key: 'week_of', label: 'Week Of', render: (r) => fmtDate(r.week_of) },
      { key: 'mon', label: 'Mon', render: (r) => fmtHours(r.mon) },
      { key: 'tue', label: 'Tue', render: (r) => fmtHours(r.tue) },
      { key: 'wed', label: 'Wed', render: (r) => fmtHours(r.wed) },
      { key: 'thu', label: 'Thu', render: (r) => fmtHours(r.thu) },
      { key: 'fri', label: 'Fri', render: (r) => fmtHours(r.fri) },
      { key: 'sat', label: 'Sat', render: (r) => fmtHours(r.sat) },
      { key: 'sun', label: 'Sun', render: (r) => fmtHours(r.sun) },
      { key: 'total_hours', label: 'Total', render: (r) => <b className="text-white">{fmtHours(r.total_hours)}</b>, value: (r) => fmtHours(r.total_hours) },
    ];
    return commonShell(cols, null, null);
  }

  /* ───── 4) Top Performers ───── */
  if (reportKey === 'top-performers') {
    const metricKey = metric;
    const cols = [
      { key: 'worker_name', label: 'Worker' },
      { key: 'jobs_completed', label: 'Jobs Completed' },
      { key: 'hours_logged', label: 'Hours Logged', render: (r) => fmtHours(r.hours_logged) },
      { key: 'on_time_rate', label: 'On-Time %', render: (r) => `${r.on_time_rate}%` },
    ];
    const chart = rows.length > 0 ? (
      <div className="h-64 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows.slice(0, 10)}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
            <XAxis dataKey="worker_name" stroke="#A0AEC0" fontSize={11} />
            <YAxis stroke="#A0AEC0" fontSize={11} />
            <Tooltip contentStyle={{ background: '#0F1923', border: '1px solid #2D3748' }} />
            <Bar dataKey={metricKey} fill="#1E88E5" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    ) : null;
    const extras = (
      <div className="mb-2 flex items-center gap-2">
        <span className="text-xs text-slate-400">Rank by:</span>
        {[{ k: 'jobs_completed', l: 'Jobs' }, { k: 'hours_logged', l: 'Hours' }, { k: 'on_time_rate', l: 'On-Time %' }].map((m) => (
          <button key={m.k} onClick={() => setMetric(m.k)}
                  className="px-2.5 py-1 rounded-md text-xs"
                  style={{
                    background: metric === m.k ? '#1E88E5' : 'transparent',
                    color: metric === m.k ? '#fff' : '#A0AEC0',
                    border: `1px solid ${metric === m.k ? '#1E88E5' : '#2D3748'}`,
                  }}
                  data-testid={`top-performers-metric-${m.k}`}>
            {m.l}
          </button>
        ))}
      </div>
    );
    return commonShell(cols, extras, chart);
  }

  /* ───── 5) Completed Jobs ───── */
  if (reportKey === 'completed-jobs') {
    const cols = [
      { key: 'title', label: 'Job Title' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'worker_name', label: 'Worker' },
      { key: 'start_date', label: 'Start', render: (r) => fmtDate(r.start_date) },
      { key: 'completion_date', label: 'Completion', render: (r) => fmtDate(r.completion_date) },
      { key: 'total_hours', label: 'Hours', render: (r) => fmtHours(r.total_hours) },
      { key: 'service_rate', label: 'Rate', render: (r) => fmtMoney(r.service_rate) },
      { key: 'billable_amount', label: 'Billable', render: (r) => fmtMoney(r.billable_amount) },
    ];
    return commonShell(cols, null, null);
  }

  /* ───── 6) Job Status Snapshot ───── */
  if (reportKey === 'job-status-snapshot') {
    const counts = data?.status_counts || {};
    const pieData = Object.entries(counts).map(([k, v]) => ({ name: k.replace('_', ' '), value: v })).filter((d) => d.value > 0);
    const cols = [
      { key: 'title', label: 'Job Title' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'worker_name', label: 'Worker' },
      { key: 'status', label: 'Status' },
      { key: 'scheduled_completion', label: 'Due', render: (r) => fmtDate(r.scheduled_completion) },
    ];
    const chart = (
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-3">
        {Object.entries(counts).map(([k, v]) => (
          <div key={k} className="p-3 rounded-lg" style={{ background: '#1A2433', border: '1px solid #2D3748' }}>
            <div className="text-[10px] uppercase tracking-wider" style={{ color: '#64748B' }}>{k.replace('_', ' ')}</div>
            <div className="text-xl font-bold text-white">{v}</div>
          </div>
        ))}
        {pieData.length > 0 && (
          <div className="col-span-2 md:col-span-6 h-64 mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" label>
                  {pieData.map((entry, i) => <Cell key={entry.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#0F1923', border: '1px solid #2D3748' }} />
                <Legend wrapperStyle={{ color: '#CBD5E1' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
    return commonShell(cols, null, chart);
  }

  /* ───── 7) Overdue Jobs ───── */
  if (reportKey === 'overdue-jobs') {
    const cols = [
      { key: 'title', label: 'Job Title' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'worker_name', label: 'Worker' },
      { key: 'scheduled_completion', label: 'Due', render: (r) => fmtDate(r.scheduled_completion) },
      { key: 'days_overdue', label: 'Days Overdue', render: (r) => <span style={{ color: '#EF4444', fontWeight: 600 }}>{r.days_overdue}</span> },
      { key: 'status', label: 'Status' },
      { key: 'paused_reason', label: 'Pause Reason', render: (r) => r.paused_reason || '-' },
    ];
    return commonShell(cols, null, null);
  }

  /* ───── 8) Paused Jobs ───── */
  if (reportKey === 'paused-jobs') {
    const cols = [
      { key: 'title', label: 'Job Title' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'worker_name', label: 'Worker' },
      { key: 'paused_at', label: 'Paused At', render: (r) => fmtDT(r.paused_at) },
      { key: 'paused_reason', label: 'Reason' },
      { key: 'paused_notes', label: 'Notes', render: (r) => r.paused_notes || '-' },
      { key: 'duration_days', label: 'Days Paused' },
      { key: 'resumed_at', label: 'Resumed', render: (r) => fmtDT(r.resumed_at) },
      { key: 'supervisor_notified', label: 'Notified', render: (r) => r.supervisor_notified ? 'Yes' : 'No' },
    ];
    return commonShell(cols, null, null);
  }

  /* ───── 9) Customer Job History ───── */
  if (reportKey === 'hours-by-location') {
    const sum = data?.summary || {};
    const internal = customerCopy;
    const cols = [
      { key: 'location', label: 'Location', render: (r) => <span className="font-medium">{r.location}</span> },
      { key: 'city', label: 'City' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'jobs', label: 'Jobs', render: (r) => (
        <button type="button" onClick={() => setLocExpanded(locExpanded === r.id ? null : r.id)} className="text-sky-300 hover:underline" data-testid={`loc-expand-${r.id}`}>
          {r.jobs} job{r.jobs === 1 ? '' : 's'} {locExpanded === r.id ? '▴' : '▾'}
        </button>), noDrill: true },
      ...(internal ? [] : [{ key: 'workers', label: 'Workers' }, { key: 'worker_names', label: 'Crew', noDrill: true }]),
      { key: 'hours', label: 'Hours', render: (r) => fmtHours(r.hours) },
      ...(internal ? [] : [{ key: 'no_rate_hours', label: 'Hours (no rate)', render: (r) => (r.no_rate_hours ? fmtHours(r.no_rate_hours) : '—') }]),
      { key: 'billable_amount', label: 'Billable', render: (r) => fmtMoney(r.billable_amount) },
      { key: 'invoiced_amount', label: 'Invoiced', render: (r) => fmtMoney(r.invoiced_amount) },
      { key: 'unbilled_amount', label: 'Unbilled', render: (r) => fmtMoney(r.unbilled_amount), cellStyle: (r) => (r.unbilled_amount > 0 ? { color: '#FFA000' } : {}) },
    ];
    const jobCols = [
      { key: 'job_number', label: 'Job #' },
      { key: 'title', label: 'Job' },
      { key: 'description', label: 'Service', noDrill: true, render: (r) => <span className="text-slate-300" title={r.description}>{(r.description || '—').slice(0, 60)}{(r.description || '').length > 60 ? '…' : ''}</span> },
      { key: 'labels', label: 'Type / labels', noDrill: true, render: (r) => (r.labels?.length ? r.labels.join(', ') : '—'), exportValue: (r) => (r.labels || []).join(', ') },
      { key: 'status', label: 'Status' },
      { key: 'started_at', label: 'Started', render: (r) => fmtDate(r.started_at), exportValue: (r) => fmtDate(r.started_at) },
      { key: 'completed_at', label: 'Completed', render: (r) => fmtDate(r.completed_at), exportValue: (r) => fmtDate(r.completed_at) },
      { key: 'hours', label: 'Hours', render: (r) => fmtHours(r.hours) },
      ...(internal ? [] : [{ key: 'service_rate', label: 'Rate', render: (r) => (r.service_rate ? `${fmtMoney(r.service_rate)}${r.billing_mode === 'flat' ? ' flat' : '/h'}` : '—') }]),
      { key: 'billable_amount', label: 'Billable', render: (r) => fmtMoney(r.billable_amount) },
      { key: 'invoice_number', label: 'Invoice #', render: (r) => r.invoice_number || '—' },
      { key: 'invoice_status', label: 'Invoice status', render: (r) => r.invoice_status || '—' },
      ...(internal ? [] : [{ key: 'workers', label: 'Crew', noDrill: true, render: (r) => (r.workers || []).join(', ') || '—', exportValue: (r) => (r.workers || []).join(', ') }]),
    ];
    const expanded = rows.find((r) => r.id === locExpanded);
    const jobRows = (expanded?.jobs_detail || []).map((j) => ({ ...j, job_id: j.id }));
    const summaryTiles = [
      { label: 'Locations', value: String(sum.locations ?? 0) }, { label: 'Jobs', value: String(sum.jobs ?? 0) }, { label: 'Hours', value: fmtHours(sum.hours) },
      { label: 'Billable', value: fmtMoney(sum.billable_amount) }, { label: 'Invoiced', value: fmtMoney(sum.invoiced_amount) }, { label: 'Unbilled', value: fmtMoney(sum.unbilled_amount) }];
    return (
      <>
        <ReportShell
          title={customerCopy ? `${title} — customer copy` : title}
          onExportCSV={() => downloadCSV(`hours_by_location_${startDate}_${endDate}.csv`, rows, cols)}
          onPrint={() => printReport(printRef, customerCopy ? `${title} — customer copy` : title, summaryTiles)}
          onPdf={() => serverPdf(rows, cols, customerCopy ? `${title} — customer copy` : title, summaryTiles)}
          onEmail={() => emailPdf(rows, cols, customerCopy ? `${title} — customer copy` : title, summaryTiles)}
          onSchedule={() => setScheduleOpen(true)}
          dataTestId="report-hours-by-location"
        >
          {emailSpec && <EmailPdfDialog open onClose={() => setEmailSpec(null)} token={token} spec={emailSpec} />}
          <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="hbl-filters">
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="px-2 py-1.5 rounded-lg text-xs" style={{ background: '#1A2433', color: '#E2E8F0', border: '1px solid #2D3748' }} data-testid="hbl-customer">
              <option value="">All customers</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input value={locQuery} onChange={(e) => setLocQuery(e.target.value)} placeholder="Search address, city or customer…" className="px-2 py-1.5 rounded-lg text-xs w-64" style={{ background: '#1A2433', color: '#E2E8F0', border: '1px solid #2D3748' }} data-testid="hbl-search" />
            <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none ml-auto" style={{ color: '#A0AEC0' }} title="Hides crew names, rates and no-rate hours — for PDFs you attach to customer billing">
              <input type="checkbox" checked={customerCopy} onChange={(e) => setCustomerCopy(e.target.checked)} className="accent-emerald-500" data-testid="hbl-customer-copy" /> Customer copy (hide internal columns)
            </label>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3" data-testid="hbl-summary">
            {summaryTiles.map((t) => (
              <div key={t.label} className="rounded-lg p-2.5" style={{ background: '#1A2433', border: '1px solid #2D3748' }}>
                <p className="text-[10px] uppercase tracking-wide" style={{ color: '#64748B' }}>{t.label}</p>
                <p className="text-base font-semibold text-white">{t.value}</p>
              </div>
            ))}
          </div>
          {rows.length > 0 && <ChartSwitcher rows={rows} columns={cols} view={chartView} onView={setChartView} chartRef={chartRef} />}
          <div ref={printRef}>
            {rows.length === 0 ? <EmptyState msg="No job hours at any location in this period." /> : <DataTable rows={rows} columns={cols} />}
            {expanded && (
              <div className="mt-4" data-testid="hbl-jobs-panel">
                <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#64748B' }}>Jobs at {expanded.location}{expanded.city ? `, ${expanded.city}` : ''} — click a job for location, service and duration details</p>
                <DataTable rows={jobRows} columns={jobCols} />
              </div>
            )}
          </div>
        </ReportShell>
        {scheduleOpen && <ScheduleModal token={token} reportKey={reportKey} reportTitle={title} onClose={() => setScheduleOpen(false)} customerId={customerId} />}
      </>
    );
  }

  if (reportKey === 'customer-job-history') {
    const cols = [
      { key: 'title', label: 'Job Title' },
      { key: 'status', label: 'Status' },
      { key: 'worker_name', label: 'Worker' },
      { key: 'start_date', label: 'Start', render: (r) => fmtDate(r.start_date) },
      { key: 'completion_date', label: 'Completion', render: (r) => fmtDate(r.completion_date) },
      { key: 'total_hours', label: 'Hours', render: (r) => fmtHours(r.total_hours) },
      { key: 'billable_amount', label: 'Billable', render: (r) => fmtMoney(r.billable_amount) },
    ];
    const extras = (
      <div className="mb-2 flex items-center gap-2">
        <label className="text-xs text-slate-400">Customer:</label>
        <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}
                className="px-2 py-1 rounded bg-[#1A2433] border border-[#2D3748] text-white text-xs"
                data-testid="customer-history-select">
          <option value="">Select a customer…</option>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
    );
    const summary = data?.summary;
    const footer = summary && rows.length > 0 ? (
      <div className="mt-3 pt-3 border-t border-[#2D3748] text-sm text-white flex gap-6">
        <div><span className="text-slate-400">Total Jobs:</span> <b>{summary.total_jobs}</b></div>
        <div><span className="text-slate-400">Total Hours:</span> <b>{fmtHours(summary.total_hours)}</b></div>
        <div><span className="text-slate-400">Total Billed:</span> <b>{fmtMoney(summary.total_billed)}</b></div>
      </div>
    ) : null;
    return (
      <>
      <ReportShell
        title={title}
        onExportCSV={() => downloadCSV(`customer-history_${customerId}.csv`, rows, cols)}
        onPrint={() => printReport(printRef, title)}
        onPdf={() => serverPdf(rows, cols)}
        onSchedule={() => setScheduleOpen(true)}
        dataTestId={`report-${reportKey}`}
      >
        {extras}
        <div ref={printRef}>
          {!customerId ? <EmptyState msg="Select a customer to view their job history." /> :
            rows.length === 0 ? <EmptyState msg="This customer has no jobs yet." /> :
            <DataTable rows={rows} columns={cols} />}
          {footer}
        </div>
      </ReportShell>
      {scheduleOpen && <ScheduleModal token={token} reportKey={reportKey} reportTitle={title} onClose={() => setScheduleOpen(false)} customerId={customerId} />}
      </>
    );
  }

  /* ───── 10) Payroll ───── */
  if (reportKey === 'payroll') {
    // Payroll Workbench (Feb 2026) — interactive inline-edit table with
    // QBO push preview + confirm flow. See PayrollWorkbench.jsx for the
    // full UI; ReportShell still wraps it so admins keep the standard
    // Export-CSV / Print / Schedule actions for the flat summary.
    const cols = [
      { key: 'worker_name', label: 'Worker' },
      { key: 'email', label: 'Email' },
      { key: 'pay_period', label: 'Pay Period' },
      { key: 'total_hours', label: 'Total Hours', render: (r) => fmtHours(r.total_hours) },
      { key: 'regular_hours', label: 'Regular', render: (r) => fmtHours(r.regular_hours) },
      { key: 'overtime_hours', label: 'Overtime', render: (r) => fmtHours(r.overtime_hours) },
      { key: 'overtime_policy', label: 'OT Policy', value: (r) => r.overtime_policy === 'averaging' ? `Averaging (${r.ot_threshold || 44}h/wk avg)` : `Standard (${r.ot_threshold || 44}h/wk)`, render: (r) => r.overtime_policy === 'averaging' ? `Averaging (${r.ot_threshold || 44}h/wk avg)` : `Standard (${r.ot_threshold || 44}h/wk)` },
      { key: 'break_minutes', label: 'Break (min)', render: (r) => r.break_minutes ? Math.round(r.break_minutes) : '—' },
      { key: 'hourly_rate', label: 'Hourly Rate', render: (r) => r.hourly_rate != null ? `$${r.hourly_rate.toFixed(2)}` : '' },
      { key: 'regular_pay', label: 'Regular Pay ($)', render: (r) => r.regular_pay != null ? r.regular_pay.toFixed(2) : '' },
      { key: 'ot_pay', label: 'OT Pay ($)', render: (r) => r.ot_pay != null ? r.ot_pay.toFixed(2) : '' },
      { key: 'gross_pay', label: 'Gross Pay ($)', render: (r) => r.gross_pay != null ? r.gross_pay.toFixed(2) : '' },
    ];

    // Daily breakdown export (Jun 2026): one row per person per day so
    // admins can edit day-by-day hours in a spreadsheet.
    const isoWeekKey = (dateStr) => {
      const d = new Date(`${dateStr}T00:00:00Z`);
      const day = (d.getUTCDay() + 6) % 7; // Mon=0
      d.setUTCDate(d.getUTCDate() - day + 3); // Thursday of this week
      const jan4 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
      const week = 1 + Math.round(((d - jan4) / 86400000 - 3 + ((jan4.getUTCDay() + 6) % 7)) / 7);
      return `${d.getUTCFullYear()}-W${week}`;
    };
    const buildDailyRows = (source) => {
      const daily = [];
      (source || rows || []).forEach((w) => {
        const byDate = {};
        const weekTotals = {};
        (w.sessions || []).forEach((s) => {
          if (!s.date) return;
          const d = byDate[s.date] || { hours: 0, sessions: 0, jobs: new Set(), edited: false, auto: false };
          d.hours += s.hours || 0;
          d.sessions += 1;
          if (s.job_title) d.jobs.add(s.job_number ? `${s.job_number} — ${s.job_title}` : s.job_title);
          else if (s.project_name) d.jobs.add(s.project_name);
          if (s.edited) d.edited = true;
          if (s.auto_timeout) d.auto = true;
          byDate[s.date] = d;
          const wk = isoWeekKey(s.date);
          weekTotals[wk] = (weekTotals[wk] || 0) + (s.hours || 0);
        });
        Object.keys(byDate).sort().forEach((date) => {
          const d = byDate[date];
          daily.push({
            worker_name: w.worker_name,
            email: w.email,
            date,
            weekday: new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long' }),
            hours: Math.round(d.hours * 100) / 100,
            hours_type: w.overtime_policy === 'averaging'
              ? `Averaging agreement (${w.ot_threshold || 44}h/wk avg)`
              : ((weekTotals[isoWeekKey(date)] || 0) > (w.ot_threshold || 44) ? `Overtime week (>${w.ot_threshold || 44}h)` : 'Regular'),
            break_minutes: Math.round((w.breaks_by_date || {})[date] || 0),
            sessions: d.sessions,
            jobs: Array.from(d.jobs).join('; '),
            edited: d.edited ? 'Yes' : '',
            auto_clock_out: d.auto ? 'Yes' : '',
          });
        });
      });
      return daily;
    };
    const dailyCols = [
      { key: 'worker_name', label: 'Worker' },
      { key: 'email', label: 'Email' },
      { key: 'date', label: 'Date' },
      { key: 'weekday', label: 'Day' },
      { key: 'hours', label: 'Hours' },
      { key: 'hours_type', label: 'Hours Type' },
      { key: 'break_minutes', label: 'Break Min' },
      { key: 'sessions', label: 'Sessions' },
      { key: 'jobs', label: 'Jobs / Projects' },
      { key: 'edited', label: 'Edited' },
      { key: 'auto_clock_out', label: 'Auto Clock-Out' },
    ];
    return (
      <>
        <ReportShell
          title={title}
          onExportCSV={async () => downloadCSV(`payroll_${startDate}_${endDate}.csv`, await fetchAllPayrollRows(), cols)}
          onPrint={() => printReport(printRef, title)}
          onPdf={async () => serverPdf(await fetchAllPayrollRows(), cols)}
          onSchedule={() => setScheduleOpen(true)}
          dataTestId={`report-${reportKey}`}
        >
          <div className="flex justify-end mb-3">
            <button
              onClick={async () => downloadCSV(`payroll_daily_${startDate}_${endDate}.csv`, buildDailyRows(await fetchAllPayrollRows()), dailyCols)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5"
              style={{ background: 'rgba(0,200,150,0.12)', color: '#00C896', border: '1px solid rgba(0,200,150,0.4)' }}
              title="One row per person per day — ideal for editing daily hours in a spreadsheet"
              data-testid="payroll-export-daily-csv"
            >
              ⬇ Export Daily CSV (per person, per day)
            </button>
          </div>
          <div ref={printRef}>
            <PayrollWorkbench
              data={data}
              token={token}
              startDate={startDate}
              endDate={endDate}
              onRefresh={fetchData}
              onPatchRow={patchPayrollRow}
              serverPaging={{ page: pwPage, query: pwQuery, onPageChange: setPwPage, onQueryChange: setPwQuery, remember: rememberPw, onReset: resetPayrollView }}
            />
          </div>
        </ReportShell>
        {scheduleOpen && <ScheduleModal token={token} reportKey={reportKey} reportTitle={title} onClose={() => setScheduleOpen(false)} customerId={customerId} />}
      </>
    );
  }

  /* ───── 10b) Unverified Clock-Ins ───── */
  if (reportKey === 'unverified-clockins') {
    const methodLabel = (m) => (m === 'unverified' ? 'Outside geofence' : 'Not verified');
    const cols = [
      { key: 'worker_name', label: 'Worker' },
      { key: 'job_number', label: 'Job #', render: (r) => r.job_number || '—' },
      { key: 'job_title', label: 'Job' },
      { key: 'started_at', label: 'Clock-In', render: (r) => fmtDT(r.started_at) },
      { key: 'method', label: 'Status', value: (r) => methodLabel(r.method), render: (r) => (
        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
              style={{ background: 'rgba(255,160,0,0.12)', color: '#FFA000', border: '1px solid rgba(255,160,0,0.4)' }}>
          {methodLabel(r.method)}
        </span>
      )},
      { key: 'distance_m', label: 'Distance', value: (r) => (r.distance_m != null ? `${r.distance_m} m` : ''), render: (r) => (r.distance_m != null ? `${r.distance_m} m from site` : '—') },
      { key: 'reason', label: 'Reason', render: (r) => r.reason || '—' },
    ];
    const banner = (
      <p className="text-xs mb-3" style={{ color: '#94A3B8' }} data-testid="unverified-clockins-banner">
        Clock-ins with no site QR scan and no GPS confirmation inside the geofence. Review anything that looks off with the worker.
      </p>
    );
    return commonShell(cols, banner, null);
  }

  /* ───── 11) Billing ───── */
  if (reportKey === 'billing') {
    const cols = [
      { key: 'title', label: 'Job Title' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'completion_date', label: 'Completion', render: (r) => fmtDate(r.completion_date) },
      { key: 'worker_name', label: 'Worker' },
      { key: 'hours_worked', label: 'Hours', render: (r) => fmtHours(r.hours_worked) },
      { key: 'service_rate', label: 'Rate', render: (r) => fmtMoney(r.service_rate) },
      { key: 'billable_amount', label: 'Billable', render: (r) => fmtMoney(r.billable_amount) },
      { key: 'qbo_status', label: 'QBO', render: (r) => (
          <span style={{ color: r.qbo_status === 'Synced' ? '#00C896' : r.qbo_status === 'No Rate Set' ? '#FFA000' : '#64748B' }}>
            {r.qbo_status}
          </span>
      ), cellStyle: (r) => r.qbo_status === 'No Rate Set' ? { background: 'rgba(255,160,0,0.06)' } : {} },
    ];
    const grand = data?.grand_total;
    const footer = grand != null && rows.length > 0 ? (
      <div className="mt-3 pt-3 border-t border-[#2D3748] text-right text-sm text-white">
        Grand Total: <b>{fmtMoney(grand)}</b>
      </div>
    ) : null;
    return (
      <>
      <ReportShell
        title={title}
        onExportCSV={() => downloadCSV(`billing_${startDate}_${endDate}.csv`, rows, cols)}
        onPrint={() => printReport(printRef, title)}
        onPdf={() => serverPdf(rows, cols)}
        onSchedule={() => setScheduleOpen(true)}
        dataTestId={`report-${reportKey}`}
      >
        <div ref={printRef}>
          {rows.length === 0 ? <EmptyState /> : <DataTable rows={rows} columns={cols} />}
          {footer}
        </div>
      </ReportShell>
      {scheduleOpen && <ScheduleModal token={token} reportKey={reportKey} reportTitle={title} onClose={() => setScheduleOpen(false)} customerId={customerId} />}
      </>
    );
  }

  /* ───── 12) Revenue by Customer ───── */
  if (reportKey === 'revenue-by-customer') {
    const cols = [
      { key: 'customer_name', label: 'Customer' },
      { key: 'jobs_completed', label: 'Jobs' },
      { key: 'total_hours', label: 'Hours', render: (r) => fmtHours(r.total_hours) },
      { key: 'total_billed', label: 'Revenue', render: (r) => fmtMoney(r.total_billed) },
      { key: 'avg_job_value', label: 'Avg Job', render: (r) => fmtMoney(r.avg_job_value) },
    ];
    const chart = rows.length > 0 ? (
      <div className="h-64 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows.slice(0, 10)}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
            <XAxis dataKey="customer_name" stroke="#A0AEC0" fontSize={11} />
            <YAxis stroke="#A0AEC0" fontSize={11} />
            <Tooltip contentStyle={{ background: '#0F1923', border: '1px solid #2D3748' }} formatter={(v) => `$${v}`} />
            <Bar dataKey="total_billed" fill="#00C896" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    ) : null;
    return commonShell(cols, null, chart);
  }

  /* ───── Hours & Billing by Label (Aug 2026) ───── */
  if (reportKey === 'label-summary') {
    const cols = [
      {
        key: 'label_name', label: 'Label',
        render: (r) => (
          <span
            className="px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide"
            style={{ background: `${r.color || '#64748B'}22`, color: r.color || '#94A3B8', border: `1px solid ${r.color || '#64748B'}55` }}
          >
            {r.label_name}
          </span>
        ),
      },
      { key: 'jobs', label: 'Jobs' },
      { key: 'entries', label: 'Shifts' },
      { key: 'hours', label: 'Hours', render: (r) => fmtHours(r.hours) },
      { key: 'billed', label: 'Billing', render: (r) => fmtMoney(r.billed) },
      { key: 'avg_rate', label: 'Avg Rate', render: (r) => (r.avg_rate ? fmtMoney(r.avg_rate) : '—') },
    ];
    const chart = rows.length > 0 ? (
      <div className="h-64 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
            <XAxis dataKey="label_name" stroke="#A0AEC0" fontSize={11} />
            <YAxis stroke="#A0AEC0" fontSize={11} />
            <Tooltip contentStyle={{ background: '#0F1923', border: '1px solid #2D3748' }} />
            <Bar dataKey="hours" name="Hours" radius={[6, 6, 0, 0]}>
              {rows.map((r) => (
                <Cell key={r.label_id} fill={r.color || '#1E88E5'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    ) : null;
    const note = (
      <p className="text-xs mb-3" style={{ color: '#64748B' }}>
        Jobs carrying multiple labels count toward each label. Billing uses each job's effective rate (label rates included).
      </p>
    );
    return commonShell(cols, note, chart);
  }

  /* ───── 13) Unbilled Jobs ───── */
  if (reportKey === 'unbilled-jobs') {
    const cols = [
      { key: 'title', label: 'Job Title' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'completion_date', label: 'Completion', render: (r) => fmtDate(r.completion_date) },
      { key: 'worker_name', label: 'Worker' },
      { key: 'hours_worked', label: 'Hours', render: (r) => fmtHours(r.hours_worked) },
      { key: 'service_rate', label: 'Rate', render: (r) => r.service_rate ? fmtMoney(r.service_rate) : <span style={{ color: '#FFA000' }}>Not set</span> },
      { key: 'actions', label: 'Action', render: (r) => (
          <a href={`/jobs?edit=${r.id}`} className="text-[#1E88E5] text-xs underline" data-testid={`set-rate-${r.id}`}>
            Set Rate &amp; Invoice
          </a>
      )},
    ];
    return commonShell(cols, null, null);
  }

  /* ───── 14) Company KPI Summary ───── */
  if (reportKey === 'company-kpi') {
    const kpis = data || {};
    const cards = [
      { label: 'Jobs Completed', value: kpis.total_jobs_completed, icon: Briefcase },
      { label: 'Completion Rate', value: `${kpis.completion_rate || 0}%`, icon: Target },
      { label: 'Avg Completion Time', value: `${kpis.avg_completion_hours || 0} h`, icon: Clock },
      { label: 'Total Hours Logged', value: fmtHours(kpis.total_hours_logged), icon: UserCheck },
      { label: 'Revenue', value: fmtMoney(kpis.total_revenue), icon: DollarSign },
      { label: 'Top Customer', value: kpis.top_customer || '—', icon: Building2 },
      { label: 'Top Worker', value: kpis.top_worker || '—', icon: Trophy },
      { label: 'Overdue Rate', value: `${kpis.overdue_rate || 0}%`, icon: AlertTriangle },
    ];
    return (
      <>
      <ReportShell
        title={title}
        onExportCSV={() => downloadCSV(`company-kpi_${startDate}_${endDate}.csv`,
          [kpis], Object.keys(kpis).map((k) => ({ key: k, label: k })))}
        onPrint={() => printReport(printRef, title)}
        onSchedule={() => setScheduleOpen(true)}
        dataTestId={`report-${reportKey}`}
      >
        <div ref={printRef}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {cards.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.label} className="p-4 rounded-xl" style={{ background: '#1A2433', border: '1px solid #2D3748' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase tracking-wider" style={{ color: '#64748B' }}>{c.label}</span>
                    <Icon className="w-4 h-4" style={{ color: '#1E88E5' }} />
                  </div>
                  <div className="text-xl font-bold text-white">{c.value}</div>
                </div>
              );
            })}
          </div>
        </div>
      </ReportShell>
      {scheduleOpen && <ScheduleModal token={token} reportKey={reportKey} reportTitle={title} onClose={() => setScheduleOpen(false)} customerId={customerId} />}
      </>
    );
  }

  /* ───── 15) Team Productivity ───── */
  if (reportKey === 'team-productivity') {
    const cols = [
      { key: 'team_name', label: 'Team' },
      { key: 'supervisor', label: 'Supervisor' },
      { key: 'workers', label: 'Workers' },
      { key: 'jobs_completed', label: 'Completed' },
      { key: 'total_hours', label: 'Hours', render: (r) => fmtHours(r.total_hours) },
      { key: 'on_time_rate', label: 'On-Time %', render: (r) => `${r.on_time_rate}%` },
      { key: 'avg_hours_per_job', label: 'Avg h/Job', render: (r) => fmtHours(r.avg_hours_per_job) },
    ];
    const chart = rows.length > 0 ? (
      <div className="h-64 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
            <XAxis dataKey="team_name" stroke="#A0AEC0" fontSize={11} />
            <YAxis stroke="#A0AEC0" fontSize={11} />
            <Tooltip contentStyle={{ background: '#0F1923', border: '1px solid #2D3748' }} />
            <Bar dataKey="jobs_completed" fill="#A78BFA" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    ) : null;
    return commonShell(cols, null, chart);
  }

  /* ───── 16) Monthly Trend ───── */
  if (reportKey === 'monthly-trend') {
    const cols = [
      { key: 'label', label: 'Month' },
      { key: 'jobs_completed', label: 'Jobs' },
      { key: 'total_hours', label: 'Hours', render: (r) => fmtHours(r.total_hours) },
      { key: 'revenue', label: 'Revenue', render: (r) => fmtMoney(r.revenue) },
      { key: 'active_workers', label: 'Active Workers' },
    ];
    const chart = rows.length > 0 ? (
      <div className="h-64 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
            <XAxis dataKey="label" stroke="#A0AEC0" fontSize={11} />
            <YAxis stroke="#A0AEC0" fontSize={11} />
            <Tooltip contentStyle={{ background: '#0F1923', border: '1px solid #2D3748' }} />
            <Legend wrapperStyle={{ color: '#CBD5E1' }} />
            <Line type="monotone" dataKey="jobs_completed" stroke="#1E88E5" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="total_hours" stroke="#00C896" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="revenue" stroke="#A78BFA" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="active_workers" stroke="#FFA000" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    ) : null;
    return commonShell(cols, null, chart);
  }

  // ─── HR & Executive: Team Attendance Heatmap ───
  if (reportKey === 'team-attendance-heatmap') {
    const weeks = data?.weeks || heatmapWeeks;
    const colorForRate = (rate) => {
      if (rate >= 85) return 'rgba(0,200,150,0.6)';
      if (rate >= 65) return 'rgba(255,160,0,0.55)';
      if (rate >= 30) return 'rgba(239,68,68,0.45)';
      return 'rgba(100,116,139,0.3)';
    };
    const cols = [
      { key: 'team_name', label: 'Team' },
      { key: 'members', label: 'Members' },
      { key: 'avg_rate', label: 'Avg Attendance %', render: (r) => {
          const avg = r.weeks.length ? r.weeks.reduce((s, w) => s + w.rate, 0) / r.weeks.length : 0;
          return `${avg.toFixed(1)}%`;
        } },
    ];
    const extras = (
      <div className="flex items-center gap-2 mb-3">
        <label className="text-xs" style={{ color: '#A0AEC0' }}>Weeks:</label>
        <select value={heatmapWeeks} onChange={(e) => setHeatmapWeeks(Number(e.target.value))} className="px-2 py-1.5 rounded-lg text-xs" style={{ background: '#1A2433', color: '#fff', border: '1px solid #2D3748' }}>
          {[4, 8, 12, 16, 26].map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
      </div>
    );
    const chart = rows.length > 0 ? (
      <div className="overflow-x-auto mb-4">
        <table className="text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th className="sticky left-0 text-left py-2 px-2 font-medium" style={{ background: '#1A2433', color: '#64748B', borderBottom: '1px solid #2D3748', minWidth: 140 }}>Team</th>
              {Array.from({ length: weeks }).map((_, w) => (
                <th key={`week-${w}`} className="text-center py-2 px-1.5 font-medium" style={{ color: '#64748B', borderBottom: '1px solid #2D3748', fontSize: 10 }}>
                  W{w + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.team_id}>
                <td className="sticky left-0 py-1.5 px-2 text-white font-medium" style={{ background: '#0F1923', borderBottom: '1px solid #1F2D3F', minWidth: 140 }}>{r.team_name}</td>
                {r.weeks.map((w, i) => (
                  <td key={w.week_start || `week-${i}`} className="text-center py-1.5 px-1" style={{ borderBottom: '1px solid #1F2D3F' }}
                      title={`${w.week_start} — ${w.rate}% (${w.present}/${r.members})`}>
                    <div className="mx-auto rounded" style={{ background: colorForRate(w.rate), width: 28, height: 20, color: '#fff', fontSize: 10, lineHeight: '20px' }}>
                      {w.rate > 0 ? Math.round(w.rate) : ''}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center gap-3 mt-3 text-[10px]" style={{ color: '#64748B' }}>
          <span className="inline-flex items-center gap-1"><span style={{ background: 'rgba(0,200,150,0.6)', width: 12, height: 12, display: 'inline-block', borderRadius: 2 }} /> ≥85%</span>
          <span className="inline-flex items-center gap-1"><span style={{ background: 'rgba(255,160,0,0.55)', width: 12, height: 12, display: 'inline-block', borderRadius: 2 }} /> 65–84%</span>
          <span className="inline-flex items-center gap-1"><span style={{ background: 'rgba(239,68,68,0.45)', width: 12, height: 12, display: 'inline-block', borderRadius: 2 }} /> 30–64%</span>
          <span className="inline-flex items-center gap-1"><span style={{ background: 'rgba(100,116,139,0.3)', width: 12, height: 12, display: 'inline-block', borderRadius: 2 }} /> &lt;30%</span>
        </div>
      </div>
    ) : null;
    return commonShell(cols, extras, chart);
  }

  // ─── HR & Executive: Worker Variance ───
  if (reportKey === 'worker-variance') {
    const varColor = (v) => {
      if (v == null) return '#A0AEC0';
      if (v >= 15)  return '#00C896';
      if (v <= -15) return '#EF4444';
      return '#FFA000';
    };
    const cols = [
      { key: 'name', label: 'Worker' },
      { key: 'hours', label: 'Hours', render: (r) => fmtHours(r.hours) },
      { key: 'team_hours_avg', label: 'Team Avg Hours', render: (r) => fmtHours(r.team_hours_avg) },
      { key: 'hours_variance_pct', label: 'Hours Variance', render: (r) =>
          r.hours_variance_pct == null ? '—' :
          <span style={{ color: varColor(r.hours_variance_pct), fontWeight: 600 }}>{r.hours_variance_pct > 0 ? '+' : ''}{r.hours_variance_pct}%</span> },
      { key: 'jobs', label: 'Jobs Done' },
      { key: 'team_jobs_avg', label: 'Team Avg Jobs' },
      { key: 'jobs_variance_pct', label: 'Jobs Variance', render: (r) =>
          r.jobs_variance_pct == null ? '—' :
          <span style={{ color: varColor(r.jobs_variance_pct), fontWeight: 600 }}>{r.jobs_variance_pct > 0 ? '+' : ''}{r.jobs_variance_pct}%</span> },
    ];
    return commonShell(cols, null, null);
  }

  // ─── HR & Executive: Team Scorecard ───
  if (reportKey === 'team-scorecard') {
    const cols = [
      { key: 'team_name', label: 'Team' },
      { key: 'members', label: 'Members' },
      { key: 'total_hours', label: 'Hours', render: (r) => fmtHours(r.total_hours) },
      { key: 'jobs_completed', label: 'Jobs Completed' },
      { key: 'on_time_pct', label: 'On-Time %', render: (r) => `${r.on_time_pct}%` },
      { key: 'overdue_jobs', label: 'Overdue' },
      { key: 'utilization_pct', label: 'Utilization %', render: (r) => {
          const c = r.utilization_pct >= 75 ? '#00C896' : r.utilization_pct >= 50 ? '#FFA000' : '#EF4444';
          return <span style={{ color: c, fontWeight: 600 }}>{r.utilization_pct}%</span>;
        } },
    ];
    const chart = rows.length > 0 ? (
      <div className="h-64 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
            <XAxis dataKey="team_name" stroke="#A0AEC0" fontSize={11} />
            <YAxis stroke="#A0AEC0" fontSize={11} />
            <Tooltip contentStyle={{ background: '#0F1923', border: '1px solid #2D3748' }} />
            <Legend wrapperStyle={{ color: '#CBD5E1' }} />
            <Bar dataKey="utilization_pct" fill="#1E88E5" name="Utilization %" />
            <Bar dataKey="on_time_pct" fill="#00C896" name="On-Time %" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    ) : null;
    return commonShell(cols, null, chart);
  }

  // ─── HR & Executive: Worker Historical Trend ───
  if (reportKey === 'worker-trend') {
    const cols = [
      { key: 'label', label: 'Month' },
      { key: 'hours', label: 'Hours', render: (r) => fmtHours(r.hours) },
      { key: 'jobs_completed', label: 'Jobs Completed' },
      { key: 'attendance_pct', label: 'Attendance %', render: (r) => `${r.attendance_pct}%` },
    ];
    const extras = (
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <label className="text-xs" style={{ color: '#A0AEC0' }}>Worker:</label>
        <select value={selectedWorker} onChange={(e) => setSelectedWorker(e.target.value)} className="px-2 py-1.5 rounded-lg text-xs min-w-[220px]" style={{ background: '#1A2433', color: '#fff', border: '1px solid #2D3748' }} data-testid="worker-trend-picker">
          <option value="">— Select a worker —</option>
          {workers.map((w) => <option key={w.id} value={w.id}>{w.name} ({w.role})</option>)}
        </select>
        <label className="text-xs ml-3" style={{ color: '#A0AEC0' }}>Months:</label>
        <select value={trendMonths} onChange={(e) => setTrendMonths(Number(e.target.value))} className="px-2 py-1.5 rounded-lg text-xs" style={{ background: '#1A2433', color: '#fff', border: '1px solid #2D3748' }}>
          {[3, 6, 9, 12, 18, 24].map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        {data?.worker && (
          <span className="text-xs ml-auto" style={{ color: '#64748B' }}>Showing: <span className="text-white">{data.worker.name}</span></span>
        )}
      </div>
    );
    if (!selectedWorker) return <ReportShell title={title}><div className="p-4 text-sm" style={{ color: '#A0AEC0' }}>{extras}<p className="mt-4">Select a worker above to view their 6-month trend.</p></div></ReportShell>;
    const chart = rows.length > 0 ? (
      <div className="h-64 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
            <XAxis dataKey="label" stroke="#A0AEC0" fontSize={11} />
            <YAxis stroke="#A0AEC0" fontSize={11} />
            <Tooltip contentStyle={{ background: '#0F1923', border: '1px solid #2D3748' }} />
            <Legend wrapperStyle={{ color: '#CBD5E1' }} />
            <Line type="monotone" dataKey="hours" stroke="#00C896" strokeWidth={2} dot={{ r: 3 }} name="Hours" />
            <Line type="monotone" dataKey="jobs_completed" stroke="#1E88E5" strokeWidth={2} dot={{ r: 3 }} name="Jobs Completed" />
            <Line type="monotone" dataKey="attendance_pct" stroke="#A78BFA" strokeWidth={2} dot={{ r: 3 }} name="Attendance %" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    ) : null;
    return commonShell(cols, extras, chart);
  }

  // ─── HR & Executive: Cost per Job by Team ───
  if (reportKey === 'cost-per-job-by-team') {
    const cols = [
      { key: 'team_name', label: 'Team' },
      { key: 'jobs_completed', label: 'Jobs' },
      { key: 'labor_cost', label: 'Labor Cost', render: (r) => fmtMoney(r.labor_cost) },
      { key: 'cost_per_job', label: 'Cost / Job', render: (r) => r.cost_per_job == null ? '—' : fmtMoney(r.cost_per_job) },
      { key: 'revenue', label: 'Revenue', render: (r) => fmtMoney(r.revenue) },
      { key: 'margin', label: 'Margin', render: (r) =>
          <span style={{ color: r.margin >= 0 ? '#00C896' : '#EF4444', fontWeight: 600 }}>{fmtMoney(r.margin)}</span> },
    ];
    const chart = rows.length > 0 ? (
      <div className="h-64 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
            <XAxis dataKey="team_name" stroke="#A0AEC0" fontSize={11} />
            <YAxis stroke="#A0AEC0" fontSize={11} />
            <Tooltip contentStyle={{ background: '#0F1923', border: '1px solid #2D3748' }} />
            <Legend wrapperStyle={{ color: '#CBD5E1' }} />
            <Bar dataKey="labor_cost" fill="#EF4444" name="Labor Cost" />
            <Bar dataKey="revenue" fill="#00C896" name="Revenue" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    ) : null;
    return commonShell(cols, null, chart);
  }

  // ─── HR & Executive: Tenure & Turnover ───
  if (reportKey === 'tenure-turnover') {
    const teamSummary = data?.team_summary || [];
    const joiners = data?.joiners_last_90d || [];
    const leavers = data?.leavers_last_90d || [];
    const totals = data?.totals || {};
    const cols = [
      { key: 'team_name', label: 'Team' },
      { key: 'members', label: 'Active Members' },
      { key: 'avg_tenure_months', label: 'Avg Tenure (months)' },
      { key: 'avg_tenure_days', label: 'Avg Tenure (days)' },
    ];
    return (
      <>
        <ReportShell
          title={title}
          onExportCSV={() => downloadCSV(`tenure-turnover.csv`, teamSummary, cols)}
          onPrint={() => printReport(printRef, title)}
          onPdf={() => serverPdf(teamSummary, cols)}
          onSchedule={() => setScheduleOpen(true)}
          dataTestId={`report-${reportKey}`}
        >
          <div ref={printRef}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              {[
                ['Active', totals.active_employees],
                ['Joiners (90d)', totals.joiners_90d],
                ['Leavers (90d)', totals.leavers_90d],
                ['Net Change', totals.net_change_90d],
              ].map(([k, v]) => (
                <div key={k} className="ht-card p-3">
                  <p className="text-[11px] uppercase tracking-wide" style={{ color: '#64748B' }}>{k}</p>
                  <p className="text-xl font-bold text-white mt-1">{v ?? '—'}</p>
                </div>
              ))}
            </div>
            <h3 className="text-white font-semibold mb-2 text-sm">Team Tenure Summary</h3>
            {teamSummary.length === 0 ? <EmptyState /> : <DataTable rows={teamSummary} columns={cols} />}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
              <div>
                <h3 className="text-white font-semibold mb-2 text-sm">Joiners (last 90 days)</h3>
                {joiners.length === 0 ? <p className="text-xs" style={{ color: '#64748B' }}>None.</p> : (
                  <DataTable rows={joiners} columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'joined_at', label: 'Joined', render: (r) => new Date(r.joined_at).toLocaleDateString() },
                    { key: 'days_ago', label: 'Days Ago' },
                  ]} />
                )}
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2 text-sm">Leavers (last 90 days)</h3>
                {leavers.length === 0 ? <p className="text-xs" style={{ color: '#64748B' }}>None.</p> : (
                  <DataTable rows={leavers} columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'left_at', label: 'Left', render: (r) => new Date(r.left_at).toLocaleDateString() },
                    { key: 'days_ago', label: 'Days Ago' },
                  ]} />
                )}
              </div>
            </div>
          </div>
        </ReportShell>
        {scheduleOpen && <ScheduleModal token={token} reportKey={reportKey} reportTitle={title} onClose={() => setScheduleOpen(false)} customerId={customerId} />}
      </>
    );
  }

  /* ───── Invoiced Jobs — what got pushed to QBO in a date range ───── */
  if (reportKey === 'invoiced-jobs') {
    const cols = [
      { key: 'invoiced_at', label: 'Invoice Date', render: (r) => fmtDT(r.invoiced_at) },
      {
        key: 'qb_invoice_doc_number',
        label: 'QBO Invoice #',
        render: (r) => r.qb_invoice_id ? (
          <a
            href={`https://app.qbo.intuit.com/app/invoice?txnId=${encodeURIComponent(r.qb_invoice_id)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-600 dark:text-emerald-400 hover:underline font-mono"
            data-testid={`invoiced-qbo-link-${r.id}`}
          >
            #{r.qb_invoice_doc_number} ↗
          </a>
        ) : '—',
      },
      { key: 'job_number', label: 'Job #' },
      { key: 'title', label: 'Job' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'worker_name', label: 'Worker' },
      { key: 'completed_at', label: 'Completed', render: (r) => fmtDT(r.completed_at) },
      { key: 'hours_worked', label: 'Hrs', render: (r) => Number(r.hours_worked || 0).toFixed(2) },
      { key: 'service_rate', label: 'Rate', render: (r) => `$${Number(r.service_rate || 0).toFixed(2)}` },
      { key: 'billable_amount', label: 'Billed', render: (r) => `$${Number(r.billable_amount || 0).toFixed(2)}` },
    ];
    const extras = (
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-500" />
          <input
            type="text"
            value={invoicedQuery}
            onChange={(e) => setInvoicedQuery(e.target.value)}
            placeholder="Search invoice #, job, customer, worker, address…"
            className="w-full pl-8 pr-3 py-1.5 rounded-md text-xs bg-[#0F1923] border border-[#2D3748] text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-[#1E88E5]"
            data-testid="invoiced-report-search"
          />
        </div>
        {invoicedQuery && (
          <button
            type="button"
            onClick={() => setInvoicedQuery('')}
            className="px-2.5 py-1.5 rounded-md text-xs text-slate-400 border border-[#2D3748] hover:text-white hover:border-rose-500"
            data-testid="invoiced-report-clear"
          >
            Clear
          </button>
        )}
        <span className="text-[11px] text-slate-500 ml-auto">
          {data?.total != null ? `${data.total} invoice${data.total === 1 ? '' : 's'}` : ''}
          {data?.grand_total != null && (
            <span className="ml-3 text-emerald-400 font-semibold">
              Total billed: ${Number(data.grand_total).toFixed(2)}
            </span>
          )}
        </span>
      </div>
    );
    return commonShell(cols, extras, null);
  }

  /* ───── Safety Compliance — searchable record of OH&S attestations ───── */
  if (reportKey === 'safety-attestations') {
    const cols = [
      { key: 'accepted_at', label: 'Accepted', render: (r) => fmtDT(r.accepted_at) },
      { key: 'attester_name', label: 'Attester' },
      { key: 'team_name', label: 'Team', render: (r) => r.accepted_for_team ? (r.team_name || '—') : '—' },
      { key: 'job_number', label: 'Job #' },
      { key: 'title', label: 'Job' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'address', label: 'Address' },
      { key: 'version', label: 'v', render: (r) => <span className="font-mono text-xs">v{r.version}</span> },
      { key: 'items_count', label: '# items' },
      { key: 'started_at', label: 'Started', render: (r) => fmtDT(r.started_at) },
      { key: 'completed_at', label: 'Completed', render: (r) => fmtDT(r.completed_at) },
    ];
    const extras = (
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-500" />
          <input
            type="text"
            value={safetyQuery}
            onChange={(e) => setSafetyQuery(e.target.value)}
            placeholder="Search worker, job, team, address…"
            className="w-full pl-8 pr-3 py-1.5 rounded-md text-xs bg-[#0F1923] border border-[#2D3748] text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-[#1E88E5]"
            data-testid="safety-report-search"
          />
        </div>
        <select
          value={safetyWorker}
          onChange={(e) => setSafetyWorker(e.target.value)}
          className="px-2.5 py-1.5 rounded-md text-xs bg-[#0F1923] border border-[#2D3748] text-slate-200"
          data-testid="safety-report-worker"
        >
          <option value="">All workers</option>
          {workers.map((w) => (
            <option key={w.id} value={w.id}>{w.name}</option>
          ))}
        </select>
        <input
          type="number"
          min="1"
          value={safetyVersion}
          onChange={(e) => setSafetyVersion(e.target.value)}
          placeholder="v# (any)"
          className="w-20 px-2 py-1.5 rounded-md text-xs bg-[#0F1923] border border-[#2D3748] text-slate-200"
          data-testid="safety-report-version"
        />
        {(safetyQuery || safetyWorker || safetyVersion) && (
          <button
            type="button"
            onClick={() => { setSafetyQuery(''); setSafetyWorker(''); setSafetyVersion(''); }}
            className="px-2.5 py-1.5 rounded-md text-xs text-slate-400 border border-[#2D3748] hover:text-white hover:border-rose-500"
            data-testid="safety-report-clear"
          >
            Clear
          </button>
        )}
        <span className="text-[11px] text-slate-500 ml-auto">
          {data?.total != null ? `${data.total} attestation${data.total === 1 ? '' : 's'}` : ''}
        </span>
      </div>
    );
    return commonShell(cols, extras, null);
  }

  /* ───── Invoice Register (Sep 2026) — every numbered invoice, sorted by number ───── */
  if (reportKey === 'invoice-register') {
    const money = (v) => (v == null ? '' : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    const cols = [
      { key: 'number', label: 'Invoice #', render: (r) => <span className="font-mono">{r.number}</span> },
      { key: 'date', label: 'Date' },
      { key: 'kind', label: 'Type' },
      { key: 'job_number', label: 'Job #' },
      { key: 'customer', label: 'Customer' },
      { key: 'subtotal', label: 'Subtotal', render: (r) => money(r.subtotal) },
      { key: 'tax', label: 'Tax', render: (r) => money(r.tax) },
      { key: 'total', label: 'Total', render: (r) => <span className="font-semibold">{money(r.total)}</span> },
      { key: 'currency', label: 'Currency' },
      { key: 'status', label: 'Status' },
      { key: 'qbo_number', label: 'QBO #', render: (r) => r.qbo_number ? <span className={r.qbo_match === 'no' ? 'text-amber-400' : ''} title={r.qbo_match === 'no' ? 'QuickBooks kept a different number' : ''}>{r.qbo_number}{r.qbo_match === 'no' ? ' ≠' : ''}</span> : '—' },
      { key: 'renumbered', label: 'Renumbered' },
    ];
    const downloadXlsx = async () => {
      try {
        const res = await axios.get(`${API}/reports/v2/invoice-register.xlsx`, {
          params: { start_date: startDate, end_date: endDate }, responseType: 'blob', headers: { Authorization: `Bearer ${token}` },
        });
        const url = URL.createObjectURL(res.data); const a = document.createElement('a');
        a.href = url; a.download = `invoice-register_${startDate || 'all'}_${endDate || 'all'}.xlsx`; a.click(); URL.revokeObjectURL(url);
      } catch (e) { window.alert('Could not download the invoice register'); }
    };
    const extras = (
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-[#2D3748]" data-testid="invoice-register-bar">
        <span className="text-[11px] text-slate-400">
          {data?.summary ? `${data.summary.count} invoice${data.summary.count === 1 ? '' : 's'} · total ${money(data.summary.total)}` : ''} — sorted by number so gaps stand out.
        </span>
        <button type="button" onClick={downloadXlsx}
                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{ background: '#1E88E5', color: 'white' }} data-testid="invoice-register-xlsx-btn">
          <Download className="w-3.5 h-3.5" /> Download XLSX
        </button>
      </div>
    );
    return commonShell(cols, extras, null, null, 'No numbered invoices in this date range.');
  }

  /* ───── Travel vs On-site Time — Feb 2026 ───────────────────────────── */
  if (reportKey === 'travel-time') {
    const KIND_LABELS = {
      travel_out: 'To site',
      travel_return: 'Back to shop',
      parts_pickup: 'Parts run',
    };
    const t = data?.totals || {};
    const cols = [
      { key: 'started_at', label: 'Started', render: (r) => fmtDT(r.started_at) },
      { key: 'worker_name', label: 'Worker' },
      {
        key: 'kind',
        label: 'Kind',
        render: (r) => (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-200">
            <Truck className="w-3 h-3" /> {KIND_LABELS[r.kind] || r.kind}
          </span>
        ),
      },
      {
        key: 'duration_hours',
        label: 'Hours',
        render: (r) => <span className="font-mono">{(r.duration_hours || 0).toFixed(2)}</span>,
      },
      { key: 'job_number', label: 'Job #' },
      { key: 'title', label: 'Job' },
      { key: 'customer_name', label: 'Customer' },
      {
        key: 'reason',
        label: 'Reason',
        render: (r) => r.reason ? (
          <span className="text-slate-300">
            {r.reason.replace(/_/g, ' ')}{r.notes ? ` — ${r.notes}` : ''}
          </span>
        ) : '—',
      },
      {
        key: 'created_via',
        label: 'Source',
        render: (r) => r.created_via === 'auto_outside_fence' ? (
          <span className="text-amber-400 text-xs font-semibold">auto override</span>
        ) : (
          <span className="text-slate-500 text-xs">manual</span>
        ),
      },
      { key: 'ended_at', label: 'Ended', render: (r) => fmtDT(r.ended_at) },
    ];

    const summaryTile = (label, value, accent, Icon) => (
      <div
        className={`rounded-lg p-3 border bg-${accent}-500/5 border-${accent}-500/30`}
        style={{
          background: `rgba(${accent === 'amber' ? '245,158,11' : accent === 'emerald' ? '16,185,129' : accent === 'sky' ? '14,165,233' : '244,63,94'},0.06)`,
          borderColor: `rgba(${accent === 'amber' ? '245,158,11' : accent === 'emerald' ? '16,185,129' : accent === 'sky' ? '14,165,233' : '244,63,94'},0.30)`,
        }}
      >
        <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
          <Icon className="w-3.5 h-3.5" /> {label}
        </div>
        <div className="text-xl font-bold text-slate-100 font-mono">{value}</div>
      </div>
    );

    const summaryCards = (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="travel-report-summary">
        {summaryTile('Travel hours', (t.travel_hours ?? 0).toFixed(2), 'amber', Truck)}
        {summaryTile('On-site hours', (t.onsite_hours ?? 0).toFixed(2), 'emerald', Clock)}
        {summaryTile('% Travel', `${(t.travel_pct ?? 0).toFixed(1)}%`, 'sky', TrendingUp)}
        {summaryTile('Auto overrides', t.auto_override_count ?? 0, 'rose', MapPin)}
      </div>
    );

    const byWorker = data?.by_worker || [];
    const workerRollup = byWorker.length > 0 ? (
      <div className="mb-4 rounded-lg border border-[#2D3748] bg-[#0F1923]" data-testid="travel-report-by-worker">
        <div className="px-3 py-2 border-b border-[#2D3748] text-xs font-semibold text-slate-300">
          By worker — sorted by travel hours
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Worker</th>
                <th className="text-right px-3 py-2">Travel h</th>
                <th className="text-right px-3 py-2">On-site h</th>
                <th className="text-right px-3 py-2">Total h</th>
                <th className="text-right px-3 py-2">% Travel</th>
                <th className="text-right px-3 py-2">Segments</th>
                <th className="text-right px-3 py-2">Auto-override</th>
              </tr>
            </thead>
            <tbody className="text-slate-200">
              {byWorker.map((w) => (
                <tr key={w.worker_id} className="border-t border-[#2D3748]">
                  <td className="px-3 py-2 font-medium">{w.worker_name}</td>
                  <td className="px-3 py-2 text-right font-mono">{(w.travel_hours || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono">{(w.onsite_hours || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono">{(w.total_hours || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={`font-mono ${
                      (w.travel_pct || 0) > 30 ? 'text-amber-400' : 'text-slate-400'
                    }`}>{(w.travel_pct || 0).toFixed(1)}%</span>
                  </td>
                  <td className="px-3 py-2 text-right text-slate-400">{w.travel_segments}</td>
                  <td className="px-3 py-2 text-right">{w.auto_override_count > 0 ? (
                    <span className="text-amber-400">{w.auto_override_count}</span>
                  ) : <span className="text-slate-600">0</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : null;

    const extras = (
      <>
        {summaryCards}
        {workerRollup}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-500" />
            <input
              type="text"
              value={travelQuery}
              onChange={(e) => setTravelQuery(e.target.value)}
              placeholder="Search worker, job, customer, reason…"
              className="w-full pl-8 pr-3 py-1.5 rounded-md text-xs bg-[#0F1923] border border-[#2D3748] text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-[#1E88E5]"
              data-testid="travel-report-search"
            />
          </div>
          <select
            value={travelWorker}
            onChange={(e) => setTravelWorker(e.target.value)}
            className="px-2.5 py-1.5 rounded-md text-xs bg-[#0F1923] border border-[#2D3748] text-slate-200"
            data-testid="travel-report-worker"
          >
            <option value="">All workers</option>
            {workers.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
          <select
            value={travelKind}
            onChange={(e) => setTravelKind(e.target.value)}
            className="px-2.5 py-1.5 rounded-md text-xs bg-[#0F1923] border border-[#2D3748] text-slate-200"
            data-testid="travel-report-kind"
          >
            <option value="">Any kind</option>
            <option value="travel_out">To site</option>
            <option value="travel_return">Back to shop</option>
            <option value="parts_pickup">Parts run</option>
          </select>
          <select
            value={travelCreatedVia}
            onChange={(e) => setTravelCreatedVia(e.target.value)}
            className="px-2.5 py-1.5 rounded-md text-xs bg-[#0F1923] border border-[#2D3748] text-slate-200"
            data-testid="travel-report-source"
          >
            <option value="">Any source</option>
            <option value="manual">Manual button</option>
            <option value="auto_outside_fence">Auto override</option>
          </select>
          {(travelQuery || travelWorker || travelKind || travelCreatedVia) && (
            <button
              type="button"
              onClick={() => { setTravelQuery(''); setTravelWorker(''); setTravelKind(''); setTravelCreatedVia(''); }}
              className="px-2.5 py-1.5 rounded-md text-xs text-slate-400 border border-[#2D3748] hover:text-white hover:border-rose-500"
              data-testid="travel-report-clear"
            >
              Clear
            </button>
          )}
          <span className="text-[11px] text-slate-500 ml-auto">
            {data?.total != null ? `${data.total} segment${data.total === 1 ? '' : 's'}` : ''}
          </span>
        </div>
      </>
    );
    return commonShell(cols, extras, null);
  }

  return <ReportShell title={title}><EmptyState /></ReportShell>;
}

/* ────────────────────────────────────────────────────────────────────────
   Schedule Email modal — create a new schedule for the current report
   ──────────────────────────────────────────────────────────────────────── */
function ScheduleModal({ token, reportKey, reportTitle, onClose, customerId, extraFilters }) {
  const [frequency, setFrequency] = useState('weekly');
  const [timeOfDay, setTimeOfDay] = useState('08:00');
  const [dayOfWeek, setDayOfWeek] = useState(1);        // Tuesday
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [dateRangeMode, setDateRangeMode] = useState('auto');
  const [recipients, setRecipients] = useState('');
  const [scheduleTz, setScheduleTz] = useState(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto'; }
    catch { return 'America/Toronto'; }
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [okMsg, setOkMsg] = useState(null);

  const save = async () => {
    setSaving(true); setErr(null); setOkMsg(null);
    const emails = recipients.split(',').map((s) => s.trim()).filter(Boolean);
    if (emails.length === 0) { setErr('At least one recipient email is required.'); setSaving(false); return; }
    try {
      const filters = { ...(extraFilters || {}) };
      if (reportKey === 'customer-job-history' && customerId) filters.customer_id = customerId;
      await axios.post(`${API}/report-schedules`, {
        report_key: reportKey,
        frequency,
        time_of_day: timeOfDay,
        timezone: scheduleTz,
        day_of_week: frequency === 'weekly' ? Number(dayOfWeek) : null,
        day_of_month: frequency === 'monthly' ? Number(dayOfMonth) : null,
        date_range_mode: dateRangeMode,
        recipients: emails,
        filters,
        active: true,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setOkMsg('Schedule saved. You can manage it under "Manage Schedules".');
      setTimeout(() => onClose(), 1200);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const dow = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      style={{ background: 'rgba(11,19,33,0.85)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="schedule-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl p-6"
        style={{ background: '#1A2433', border: '1px solid #2D3748' }}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-white">Schedule Email Delivery</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" aria-label="Close">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs mb-4" style={{ color: '#A0AEC0' }}>
          Automatically email the <b style={{ color: '#fff' }}>{reportTitle}</b> report as a CSV attachment.
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Frequency</label>
            <div className="flex gap-2">
              {['daily', 'weekly', 'monthly', 'month_end'].map((f) => (
                <button key={f} onClick={() => setFrequency(f)}
                        className="flex-1 py-2 rounded-lg text-xs font-medium"
                        style={{
                          background: frequency === f ? '#1E88E5' : 'transparent',
                          color: frequency === f ? '#fff' : '#A0AEC0',
                          border: `1px solid ${frequency === f ? '#1E88E5' : '#2D3748'}`,
                        }}
                        data-testid={`schedule-freq-${f}`}>
                  {f === 'month_end' ? 'Month-end' : f[0].toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Time (local)</label>
              <input type="time" value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)}
                     className="w-full px-3 py-2 rounded-lg text-sm text-white"
                     style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                     data-testid="schedule-time" />
              <p className="text-[10px] mt-1" style={{ color: '#64748B' }}>Timezone: <span className="text-white">{scheduleTz}</span></p>
            </div>
            {frequency === 'weekly' && (
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Day of Week</label>
                <select value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg text-sm text-white"
                        style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                        data-testid="schedule-dow">
                  {dow.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </div>
            )}
            {frequency === 'monthly' && (
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Day of Month</label>
                <input type="number" min="1" max="28" value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)}
                       className="w-full px-3 py-2 rounded-lg text-sm text-white"
                       style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                       data-testid="schedule-dom" />
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Data Window</label>
            <select value={dateRangeMode} onChange={(e) => setDateRangeMode(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm text-white"
                    style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                    data-testid="schedule-range">
              <option value="auto">Auto (match frequency)</option>
              <option value="yesterday">Yesterday</option>
              <option value="this_week">This Week</option>
              <option value="last_week">Last Week</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Recipients <span className="text-slate-500">(comma-separated)</span>
            </label>
            <input type="text" placeholder="you@example.com, supervisor@example.com"
                   value={recipients} onChange={(e) => setRecipients(e.target.value)}
                   className="w-full px-3 py-2 rounded-lg text-sm text-white"
                   style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                   data-testid="schedule-recipients" />
          </div>

          {err && <p className="text-xs" style={{ color: '#EF4444' }}>{err}</p>}
          {okMsg && <p className="text-xs" style={{ color: '#00C896' }}>{okMsg}</p>}
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} disabled={saving}
                  className="flex-1 py-2 rounded-lg font-medium"
                  style={{ background: '#2D3748', color: 'white' }}
                  data-testid="schedule-cancel">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
                  className="flex-1 py-2 rounded-lg font-medium disabled:opacity-60"
                  style={{ background: '#1E88E5', color: 'white' }}
                  data-testid="schedule-save">
            {saving ? 'Saving…' : 'Save Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   Manage Schedules list (admin only)
   ──────────────────────────────────────────────────────────────────────── */
function SchedulesManager({ token, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/report-schedules`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(r.data || []);
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const sendNow = async (id) => {
    await axios.post(`${API}/report-schedules/${id}/send-now`, {}, { headers: { Authorization: `Bearer ${token}` } });
    load();
  };
  const toggleActive = async (s) => {
    const payload = {
      report_key: s.report_key, frequency: s.frequency, time_of_day: s.time_of_day,
      day_of_week: s.day_of_week, day_of_month: s.day_of_month,
      date_range_mode: s.date_range_mode, recipients: s.recipients, filters: s.filters || {},
      active: !s.active,
    };
    await axios.put(`${API}/report-schedules/${s.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
    load();
  };
  const del = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    await axios.delete(`${API}/report-schedules/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4"
         style={{ background: 'rgba(11,19,33,0.85)', backdropFilter: 'blur(4px)' }}
         onClick={onClose}
         data-testid="schedules-manager">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-3xl rounded-2xl p-6 max-h-[80vh] overflow-y-auto"
           style={{ background: '#1A2433', border: '1px solid #2D3748' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Scheduled Report Emails</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" aria-label="Close">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <LoadingSkeleton />
        ) : rows.length === 0 ? (
          <EmptyState msg="No scheduled emails yet. Open any report and click “Schedule Email”." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid #2D3748' }}>
                  <th className="text-left px-2 py-2 text-xs text-slate-400">Report</th>
                  <th className="text-left px-2 py-2 text-xs text-slate-400">When</th>
                  <th className="text-left px-2 py-2 text-xs text-slate-400">Recipients</th>
                  <th className="text-left px-2 py-2 text-xs text-slate-400">Next Run</th>
                  <th className="text-left px-2 py-2 text-xs text-slate-400">Last</th>
                  <th className="text-right px-2 py-2 text-xs text-slate-400"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} style={{ borderBottom: '1px solid #1A2433' }}
                      data-testid={`schedule-row-${s.id}`}>
                    <td className="px-2 py-2 text-white">{s.report_title || s.report_key}</td>
                    <td className="px-2 py-2" style={{ color: '#CBD5E1' }}>
                      {s.frequency === 'daily' && `Daily at ${s.time_of_day} ${s.timezone || 'UTC'}`}
                      {s.frequency === 'weekly' && `Every ${['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][s.day_of_week ?? 0]} at ${s.time_of_day} ${s.timezone || 'UTC'}`}
                      {s.frequency === 'monthly' && `Monthly on day ${s.day_of_month || 1} at ${s.time_of_day} ${s.timezone || 'UTC'}`}
                    </td>
                    <td className="px-2 py-2 text-xs" style={{ color: '#A0AEC0' }}>{(s.recipients || []).join(', ')}</td>
                    <td className="px-2 py-2 text-xs" style={{ color: '#A0AEC0' }}>
                      {s.next_run_at ? new Date(s.next_run_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-2 py-2 text-xs">
                      <span style={{ color: s.last_status === 'ok' ? '#00C896' : s.last_status === 'failed' ? '#EF4444' : '#A0AEC0' }}>
                        {s.last_status || 'never'}
                      </span>
                      {s.last_sent_at && (
                        <div className="text-[10px]" style={{ color: '#64748B' }}>
                          {new Date(s.last_sent_at).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <button onClick={() => sendNow(s.id)}
                              className="p-1.5 rounded mr-1"
                              style={{ background: 'rgba(0,200,150,0.15)', color: '#00C896' }}
                              title="Send now" data-testid={`send-now-${s.id}`}>
                        <Send className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => toggleActive(s)}
                              className="p-1.5 rounded mr-1"
                              style={{ background: s.active ? 'rgba(30,136,229,0.15)' : 'rgba(100,116,139,0.15)', color: s.active ? '#1E88E5' : '#94A3B8' }}
                              title={s.active ? 'Pause' : 'Activate'}
                              data-testid={`toggle-${s.id}`}>
                        {s.active ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => del(s.id)}
                              className="p-1.5 rounded"
                              style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}
                              title="Delete" data-testid={`delete-${s.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
